import './App.scss';
import './styles/selection-policy.scss';

import WindowControls from './components/WindowControls/WindowControls';

// Load debug utilities in development using centralized dev mode
import { devOnly, IS_DEV } from './utils/devMode';
devOnly(() => import('./utils/debugCleanup'));

// Run one-time IDB migrations (cleanup of legacy databases, format changes)
import('./services/idb/idbMigrations').then(m => m.runIDBMigrations()).catch(() => {});

// Start periodic IDB storage monitoring (background, non-blocking)
import('./services/idb/storageMonitor').then(m => m.startStorageMonitor()).catch(() => {});

// ============================================
// UNIFIED REGISTRY INITIALIZATION
// ============================================

import { entityTypeRegistry } from './services/entityTypeRegistry';
import { toolRegistry } from './tools/registry';
// Non-visual navigator: consumes the "New Desktop" transition signal and
// navigates to the intake desktop. Mounted outside the keyed DesktopPageWrapper
// so it survives the keyed remount it triggers. Eager (not lazy) so the signal
// is always consumed without suspending.
import { DesktopTransitionLayer } from './components/Space/layouts/desktop/DesktopTransitionLayer';
// Interactive, finger-tracked "drag to switch desktops" gesture. Same
// mount-outside-the-key reasoning as DesktopTransitionLayer — a drag can
// span the navigation it eventually triggers.
import DesktopSwipeDragLayer from './components/Space/layouts/desktop/DesktopSwipeDragLayer';

// Initializes lightweight registries backed by static in-memory data / manifest
// files (entity types, tool manifests) — no dynamic app-bundle imports, so this
// is cheap enough to run unconditionally on every page, including public pages
// like /signup and /login. The heavier command registry (~60 app-module imports)
// and auth-gated registries are initialized lazily from GUILayout's mount effect
// instead, since that's the only place they're ever actually consumed.
async function initializeLocalRegistries() {
  await Promise.all([
    Promise.resolve().then(() => {
      entityTypeRegistry.loadEntityTypes();
    }).catch((error: Error) => {
      console.warn('[App] Entity type registry initialization failed (non-fatal):', error);
    }),

    toolRegistry.loadAllTools().catch((error: Error) => {
      console.warn('[App] Tool registry initialization failed (non-fatal):', error);
    }),
  ]);
}

import React, { Suspense, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { lazyWithReload } from './utils/lazyWithReload';
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import OSSignupModal from './components/modals/OSSignupModal';
import useUserStore from './stores/userStore';
import { ACCOUNT_SWITCH_TAB_ID } from './stores/multiAccountStore';

// Lazy load org payment gate
const OrgPaymentGate = lazyWithReload(() => import('./components/Billing/OrgPaymentGate'));

// Import dataspace context store for payment gate
import { useDataspaceContextStore } from './stores/dataspaceContextStore';

// Import all apps - let webpack tree-shake unused ones
const ListenApp = lazyWithReload(() => import('apps/ListenApp/ListenApp'));
const StoreApp = lazyWithReload(() => import('apps/StoreApp/StoreApp'));
const QuickLauncherApp = lazyWithReload(() => import('apps/QuickLauncherApp/QuickLauncherApp'));
const MailApp = lazyWithReload(() => import('apps/MailApp/MailApp').then(m => ({ default: m.MailApp })));
const MessagingApp = lazyWithReload(() => import('apps/MessagingApp/MessagingApp').then(m => ({ default: m.MessagingApp })));
const PasswordVaultApp = lazyWithReload(() => import('apps/PasswordVaultApp/PasswordVaultApp').then(m => ({ default: m.PasswordVaultApp })));
const InboxApp = lazyWithReload(() => import('apps/InboxApp/InboxApp').then(m => ({ default: m.InboxApp })));

function StandaloneMailAppWrapper() {
  return <MailApp windowId="standalone-mail" spaceId="default" onClose={() => {}} />;
}
function StandaloneMessagingAppWrapper() {
  return <MessagingApp windowId="standalone-messaging" spaceId="default" onClose={() => {}} />;
}

// Lazy load large layout components
const UpgradeModal = lazyWithReload(() => import('./components/UpgradeModal/UpgradeModal'));
const GlobalGuestClaimModal = lazyWithReload(() => import('./components/Guest/GlobalGuestClaimModal'));
const InviteFriendModalLazy = lazyWithReload(() => import('./components/InviteFriendModal/InviteFriendModal').then(m => ({ default: m.InviteFriendModal })));
const DesktopAppAnnouncementModalLazy = lazyWithReload(() => import('./components/DesktopAppAnnouncementModal/DesktopAppAnnouncementModal'));
const ToolConsentModal = lazyWithReload(() => import('./components/ToolConsentModal').then(m => ({ default: m.ToolConsentModal })));
const UserSettingsModal = lazyWithReload(() => import('./components/Space/SystemMenuButton/UserSettings/UserSettingsModal').then(m => ({ default: m.UserSettingsModal })));
const GUILayout = lazyWithReload(() => import('./components/GUILayout/GUILayout'));
import { SystemContextProvider } from './components/GUILayout/SystemContext';
const ScrollToTop = lazyWithReload(() => import('./components/ScrollToTop/ScrollToTop'));
const NoIndexByRoute = lazyWithReload(() => import('./components/SEO/NoIndexByRoute'));
const StandaloneAppLayout = lazyWithReload(() => import('./components/Space/layouts/StandaloneAppLayout'));
const ElectronAppLayout = lazyWithReload(() => import('./components/Space/layouts/ElectronAppLayout'));
const ToastContainer = lazyWithReload(() => import('./components/Toast/Toast').then(m => ({ default: m.ToastContainer })));
const EntityPickerHost = lazyWithReload(() => import('./components/EntityPicker/EntityPickerHost').then(m => ({ default: m.EntityPickerHost })));
import ToastStack from './components/ToastStack/ToastStack';
const UpdateToast = lazyWithReload(() => import('./components/UpdateToast/UpdateToast').then(m => ({ default: m.UpdateToast })));
const PopupHost = lazyWithReload(() => import('./popups/PopupHost').then(m => ({ default: m.PopupHost })));
const RosettaBanner = lazyWithReload(() => import('./components/UpdateToast/RosettaBanner').then(m => ({ default: m.RosettaBanner })));
const ScreenRecordingBanner = lazyWithReload(() => import('./components/UpdateToast/ScreenRecordingBanner').then(m => ({ default: m.ScreenRecordingBanner })));
import { analytics, isTrackingEnabled } from './services/analytics/analytics';
import { GA4Provider } from './services/analytics/providers/ga4';
import { PostHogProvider } from './services/analytics/providers/posthog';
import { ServerRelayProvider } from './services/analytics/providers/serverRelay';
import { LogProvider } from './services/analytics/providers/log';
import { userTrackingService } from './services/users/userTrackingService';
import { createFileLogger } from './utils/logger';
import { parseJwtPayload } from './utils/jwtUtils';
import { enableNavigationPrevention } from './utils/navigationPrevention';
import { isElectron } from './utils/electron';
import { perf } from './utils/perfInstrumentation';


import { ThemeProvider } from './contexts/ThemeContext';
// Not lazy-loaded: must be able to paint immediately, above the Suspense
// boundary below, the instant a signup navigates away (see signupBootStore.ts).
import BootAnimation from './components/Onboarding/BootAnimation/BootAnimation';
const SelfHostedLicenseGate = lazyWithReload(() => import('./components/SelfHostedLicenseGate'));
// Lazy load marketing pages for better performance
const NSFWAIWriter = lazyWithReload(() => import('pages/marketing/products/NSFWAIWriter/NSFWAIWriter'));
// PersonaPage migrated to content-renderer at /personas/[slug].tsx
const GrantWriters = lazyWithReload(() => import('pages/marketing/products/GrantWriters/GrantWriters'));
const GrantWorkspaceBuilder = lazyWithReload(() => import('pages/builders/GrantWorkspaceBuilder/GrantWorkspaceBuilder'));
const PrivateWorkspace = lazyWithReload(() => import('pages/marketing/products/PrivateWorkspace/PrivateWorkspace'));
const CreativeFreedom = lazyWithReload(() => import('pages/marketing/products/CreativeFreedom/CreativeFreedom'));
const FanfictionWriter = lazyWithReload(() => import('pages/marketing/products/FanfictionWriter/FanfictionWriter'));
// SelfPublishedBook landing page is SSR-rendered by the content-renderer at /addons/self-published-book
const SelfPublishedBookRedirect: React.FC = () => {
  React.useEffect(() => {
    window.location.replace('https://rivereditor.com/addons/self-published-book');
  }, []);
  return null;
};
const BookAddonPurchasePage = lazyWithReload(() => import('pages/marketing/products/BookAddonPurchase/BookAddonPurchasePage'));
const RFPWriters = lazyWithReload(() => import('pages/marketing/products/RFPWriters/RFPWriters'));
const AcademicWriters = lazyWithReload(() => import('pages/marketing/products/AcademicWriters/AcademicWriters'));
const HNWWriters = lazyWithReload(() => import('pages/marketing/products/HNWWriters/HNWWriters'));
const PromptLibraryApp = lazyWithReload(() => import('apps/PromptLibraryApp/PromptLibraryApp'));
const SpaceCreationPage = lazyWithReload(() => import('./pages/SpaceCreationPage'));
const NewSpaceRoute = lazyWithReload(() => import('./pages/NewSpaceRoute'));
import { useAuth } from './hooks/useAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { captureRefCode, upgradeLandingPageIfDedicated } from './services/utm/utmService';
import { useGlobalCmdKShortcut } from './hooks/useGlobalCmdKShortcut';
import { useAmbientSuggestions } from './hooks/useAmbientSuggestions';
import { useGlobalEntitySearchShortcut } from './hooks/useGlobalEntitySearchShortcut';
import { useGlobalElectronTabShortcuts } from './hooks/useGlobalElectronTabShortcuts';
import { useGlobalSpacePanelShortcuts } from './hooks/useGlobalSpacePanelShortcuts';
import { useDefaultsInitializer } from './hooks/useDefaultsInitializer';
import { useQuickEntryContextPush } from './hooks/useQuickEntryContextPush';
import { useMobileMode } from './contexts/MobileModeContext';
import { DESKTOP_ROLLOUT_CUTOFF } from './constants/desktopRollout';
const ContactsApp = lazyWithReload(() => import('apps/ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp })));
const ShowcasePage = lazyWithReload(() => import('./pages/ShowcasePage'));
const InvitePage = lazyWithReload(() => import('./pages/InvitePage').then(m => ({ default: m.InvitePage })));
const ShowcaseDocumentPage = lazyWithReload(() => import('./pages/ShowcaseDocumentPage'));
const PublicBookViewer = lazyWithReload(() => import('./pages/PublicBookViewer'));
const GoogleDriveCallbackPage = lazyWithReload(() => import('./pages/GoogleDriveCallbackPage'));
const GmailOAuthCallback = lazyWithReload(() => import('./pages/GmailOAuthCallback'));
const PlaidOAuthCallbackPage = lazyWithReload(() => import('./pages/PlaidOAuthCallbackPage'));
const BroadcastUnsubscribePageLazy = lazyWithReload(() =>
  import('./pages/BroadcastUnsubscribePage/BroadcastUnsubscribePage').then(m => ({ default: m.BroadcastUnsubscribePage }))
);
const BlogAuthCallback = lazyWithReload(() => import('./pages/BlogAuthCallback'));
const YjsDebugPanel = lazyWithReload(() => import('./components/Debug/YjsDebugPanel'));

// Load all components - webpack will tree-shake unused ones
const ProtectedRoute = lazyWithReload(() => import('./components/ProtectedRoute'));
const AdminProtectedRoute = lazyWithReload(() => import('./components/AdminProtectedRoute'));
// Lazy load for better initial bundle size
const SelfhostedLoginPage = lazyWithReload(() => import('./pages/SelfhostedLoginPage'));

const SuccessPage = lazyWithReload(() => import('./pages/SuccessPage'));
const PaymentSuccessPage = lazyWithReload(() => import('./pages/PaymentSuccessPage'));
const OrgPaymentSuccessPage = lazyWithReload(() => import('./pages/OrgPaymentSuccessPage'));
const InvitationAcceptPage = lazyWithReload(() => import('./pages/InvitationAcceptPage'));
// DownloadsPage moved to content-renderer at /downloads. SPA route redirects via CrossRuntimeRedirect.
const NotFound = lazyWithReload(() => import('./pages/NotFound'));
const CrossRuntimeRedirect = lazyWithReload(() => import('./components/CrossRuntimeRedirect'));
const SpacePageWrapper = lazyWithReload(() => import('./pages/SpacePageWrapper'));
const DesktopPageWrapper = lazyWithReload(() => import('./pages/DesktopPageWrapper'));
const MissionControl = lazyWithReload(() => import('./pages/MissionControl/MissionControl'));
const SignupPage = lazyWithReload(() => import('./pages/SignupPage'));
const LoginPage = lazyWithReload(() => import('pages/LoginPage'));
const ElectronOnboardingPage = lazyWithReload(() => import('./pages/ElectronOnboarding'));
const ElectronFirstRunPage = lazyWithReload(() => import('./pages/ElectronFirstRun/ElectronFirstRun'));
const AgentConversationPage = lazyWithReload(() => import('./components/Space/AgentChat/AgentChat'));
const AgentsHome = lazyWithReload(() => import('./pages/AgentsHome/AgentsHome'));
const AutomationsStorePage = lazyWithReload(() => import('./pages/AutomationsStorePage/AutomationsStorePage'));
const OnboardingCardPreview = lazyWithReload(() => import('./pages/OnboardingCardPreview/OnboardingCardPreview'));
const ToolCardPreview = lazyWithReload(() => import('./pages/ToolCardPreview/ToolCardPreview'));
const BackgroundPreview = lazyWithReload(() => import('./pages/BackgroundPreview/BackgroundPreview'));
const SelfHostedOnboardingPage = lazyWithReload(() => import('./pages/SelfHostedOnboarding'));

// Standalone app components - load all apps
const BookkeeperApp = lazyWithReload(() => import('./apps/BookkeeperApp/BookkeeperApp'));
const DocumentAccessWrapper = lazyWithReload(() => import('./apps/DocsApp/components/DocumentAccessWrapper'));
const AgentPanelRoot = lazyWithReload(() => import('./agents/AgentPanelRoot'));
const AgentLayout = lazyWithReload(() => import('./components/GUILayout/AgentLayout'));
const BankApp = lazyWithReload(() => import('apps/BankApp/components/BankApp'));
const RiverDriveApp = lazyWithReload(() => import('apps/RiverDriveApp/RiverDriveApp'));

// Load all apps
const TasksApp = lazyWithReload(() => import('apps/TasksApp/TasksApp'));
const AutomationsApp = lazyWithReload(() => import('apps/AutomationsApp/AutomationsApp'));
const ChatApp = lazyWithReload(() => import('apps/ChatApp/ChatApp'));
const LoginApp = lazyWithReload(() => import('./LoginApp'));

// Admin layout + route components (all lazy-loaded for code splitting)
const AdminLayout = lazyWithReload(() => import('./pages/internal/admin/AdminLayout'));
const AdminOverviewRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminOverviewRoute'));
const AdminUsersRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminUsersRoute'));
const AdminUserDetailRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminUserDetailRoute'));
const AdminEntitiesRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminEntitiesRoute'));
const AdminEntitiesKeyMetricsRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminEntitiesKeyMetricsRoute'));
const AdminEntitiesToolsRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminEntitiesToolsRoute'));
const AdminContentRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminContentRoute'));
const AdminGrowthRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminGrowthRoute'));
const AdminActivityRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminActivityRoute'));
const AdminConversionsRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminConversionsRoute'));
const AdminSignupsByPageRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminSignupsByPageRoute'));
const AdminBroadcastRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminBroadcastRoute'));
const AdminAnalyticsOverviewRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminAnalyticsOverviewRoute'));
const AdminAnalyticsMetricRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminAnalyticsMetricRoute'));
const AdminAnalyticsQAURoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminAnalyticsQAURoute'));
const AdminActivationRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminActivationRoute'));
const AdminLLMUsageRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminLLMUsageRoute'));
const AdminChatInputRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminChatInputRoute'));
const AdminUserMiniToolsRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminUserMiniToolsRoute'));
const AdminIntegrationsRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminIntegrationsRoute'));
const AdminPersonasRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminPersonasRoute'));
const AdminOverviewOnboardingRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminOverviewOnboardingRoute'));
const AdminOverviewSystemRoute = lazyWithReload(() => import('./pages/internal/admin/routes/AdminOverviewSystemRoute'));
const AdminSetup = lazyWithReload(() => import('./pages/internal/AdminSetup'));

// Marketing pages - lazy load for better initial bundle size
// RiverDocsLanding moved to content-renderer at /. SPA route redirects via CrossRuntimeRedirect.
const MediaGenApp = lazyWithReload(() => import('apps/MediaGenApp/MediaGenApp'));
const CollegeEssayLanding = lazyWithReload(() => import('pages/marketing/college-essay/CollegeEssayLanding'));
const HumanFirst = lazyWithReload(() => import('./pages/marketing/products/HumanFirst/HumanFirst'));
const ConfigureRiver1Page = lazyWithReload(() => import('./pages/marketing/products/RiverBox1/ConfigureRiver1Page'));
const HumanizerOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/HumanizerTool/HumanizerOnboarding'));
const NSFWOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/NSFWWriters/NSFWOnboarding'));
const BlogCreatorOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/BlogCreator/BlogCreatorOnboarding'));
const SEOBlogCreatorOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/SEOBlogCreator/SEOBlogCreatorOnboarding'));
const SEOOptimizerOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/SEOOptimizer/SEOOptimizerOnboarding'));
const ContentPolisherOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/ContentPolisher/ContentPolisherOnboarding'));
const ImageInserterOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/ImageInserter/ImageInserterOnboarding'));
const TemplateOnboarding = lazyWithReload(() => import('./pages/onboardingFlows/TemplateOnboarding/TemplateOnboarding'));

// Platform Landing & General Onboarding
const RiverSpacesLanding = lazyWithReload(() => import('./pages/marketing/RiverSpacesLanding/RiverSpacesLanding'));
const RiverOnboarding = lazyWithReload(() => import('./pages/Onboarding/RiverOnboarding'));

// Product Pages
const ProductsPage = lazyWithReload(() => import('./pages/marketing/products/ProductsPage'));
const AiPersonalComputer = lazyWithReload(() => import('./pages/marketing/products/AiPersonalComputer/AiPersonalComputer'));
const RiverConfigFlow = lazyWithReload(() => import('./pages/RiverConfigFlow/RiverConfigFlow'));
// WorkspacePage moved to content-renderer at /workspace. SPA route redirects via CrossRuntimeRedirect.
const WorkLanding = lazyWithReload(() => import('./pages/WorkLanding/WorkLanding'));
const ConciergeRiverLanding = lazyWithReload(() => import('./pages/ConciergeRiverLanding/ConciergeRiverLanding'));
const TwitterAgentLanding = lazyWithReload(() => import('./pages/TwitterAgentLanding/TwitterAgentLanding'));
const LinkedInGrowthLanding = lazyWithReload(() => import('./pages/LinkedInGrowthLanding/LinkedInGrowthLanding'));
const RedditAgentLanding = lazyWithReload(() => import('./pages/RedditAgentLanding/RedditAgentLanding'));
const SeoAgentLanding = lazyWithReload(() => import('./pages/SeoAgentLanding/SeoAgentLanding'));
const ClaimPack = lazyWithReload(() => import('./pages/ClaimPack/ClaimPack'));
const GuestStart = lazyWithReload(() => import('./pages/GuestStart/GuestStart'));
const ClaimToolbox = lazyWithReload(() => import('./pages/ClaimToolbox/ClaimToolbox'));
const CustomerSuccessLanding = lazyWithReload(() => import('./pages/CustomerSuccessLanding/CustomerSuccessLanding'));
const AutoResponderLanding = lazyWithReload(() => import('./pages/AutoResponderLanding/AutoResponderLanding'));
const CustomerSupportAgentLanding = lazyWithReload(() => import('./pages/CustomerSupportAgentLanding/CustomerSupportAgentLanding'));
const ChrisLanding = lazyWithReload(() => import('./pages/clients/ChrisLanding/ChrisLanding'));
const StudioClient = lazyWithReload(() => import('./pages/clients/StudioClient/StudioClient'));
const DavenportRoadhouseClient = lazyWithReload(() => import('./pages/clients/DavenportRoadhouseClient/DavenportRoadhouseClient'));
const PhysicianPracticeClient = lazyWithReload(() => import('./pages/clients/PhysicianPracticeClient/PhysicianPracticeClient'));
const AllVetCareCaseStudy = lazyWithReload(() => import('./pages/case-studies/AllVetCareCaseStudy/AllVetCareCaseStudy'));
const VdogClient = lazyWithReload(() => import('./pages/clients/VdogClient/VdogClient'));
const SomnoSealDemo = lazyWithReload(() => import('./pages/demos/SomnoSealDemo/SomnoSealDemo'));
const SomnoSealChatDemo = lazyWithReload(() => import('./pages/demos/SomnoSealDemo/SomnoSealChatDemo'));
const VentureCampDemo = lazyWithReload(() => import('./pages/demos/VentureCampDemo/VentureCampDemo'));
const VentureCampWorkspaceDemo = lazyWithReload(() => import('./pages/demos/VentureCampDemo/VentureCampWorkspaceDemo'));
const RiverLabsDemo = lazyWithReload(() => import('./pages/demos/RiverLabsDemo/RiverLabsDemo'));
const RiverLabsWorkspaceDemo = lazyWithReload(() => import('./pages/demos/RiverLabsDemo/RiverLabsWorkspaceDemo'));
const SBNephrologyDemo = lazyWithReload(() => import('./pages/demos/SBNephrologyDemo/SBNephrologyDemo'));
const SBNephrologyWorkspaceDemo = lazyWithReload(() => import('./pages/demos/SBNephrologyDemo/SBNephrologyWorkspaceDemo'));
const SBNHomeDemo = lazyWithReload(() => import('./pages/demos/HomeAsRiverOSLaunchPoint/home/SBNHomeDemo'));
const SBNDesktopDemo = lazyWithReload(() => import('./pages/demos/HomeAsRiverOSLaunchPoint/desktop/SBNDesktopDemo'));
const FloweramaColumbusDemo = lazyWithReload(() => import('./pages/demos/FloweramaColumbusDemo/FloweramaColumbusDemo'));
const FloweramaColumbusWorkspaceDemo = lazyWithReload(() => import('./pages/demos/FloweramaColumbusDemo/FloweramaColumbusWorkspaceDemo'));
const PickthornDemo = lazyWithReload(() => import('./pages/demos/PickthornDemo/PickthornDemo'));
const PickthornWorkspaceDemo = lazyWithReload(() => import('./pages/demos/PickthornDemo/PickthornWorkspaceDemo'));
const CampaignMock = lazyWithReload(() => import('./pages/mocks/CampaignMock/CampaignMock'));

const TasksAppPage = lazyWithReload(() => import('./pages/marketing/products/TasksPage/TasksPage'));
const OSSignupFlow = lazyWithReload(() => import('./pages/OSSignup/OSSignupFlow'));
const CheckoutPage = lazyWithReload(() => import('./pages/CheckoutPage'));

// Labs pages (internal experiments — served at /labs/*, noindex)
const AvatarPage  = lazyWithReload(() => import('./pages/labs/AvatarPage'));
const AvatarPage2 = lazyWithReload(() => import('./pages/labs/AvatarPage2'));
const AvatarPage3 = lazyWithReload(() => import('./pages/labs/AvatarPage3'));
const RiverBackgrounds = lazyWithReload(() => import('./pages/labs/RiverBackgrounds/RiverBackgrounds'));

// Enterprise Pages
const LawyerDocsEditor = lazyWithReload(() => import('./pages/marketing/enterprise/lawyers/LawyerDocsEditor'));

// Use Case Pages
const PreLawDeadlinePanic = lazyWithReload(() => import('./pages/marketing/use-cases/PreLawDeadlinePanic'));

// Converter Pages
const PdfToWordConverter = lazyWithReload(() => import('./pages/marketing/convert/pdf-to-word/PdfToWordConverter'));
const ManuscriptDiscovery = lazyWithReload(() => import('./pages/marketing/manuscript-feedback/ManuscriptDiscovery'));
const ManuscriptAnalysisProgress = lazyWithReload(() => import('./pages/marketing/manuscript-feedback/ManuscriptAnalysisProgress'));
const ChatGPTToWordConverter = lazyWithReload(() => import('./pages/marketing/convert/chatgpt-to-word/ChatGPTToWordConverter'));
const MarkdownToWordConverter = lazyWithReload(() => import('./pages/marketing/convert/markdown-to-word/MarkdownToWordConverter'));

// Tool Pages
const YouTubeScriptCreator = lazyWithReload(() => import('./pages/marketing/tools/youtube-script-creator/YouTubeScriptCreator'));
const AIProductDescriptionGenerator = lazyWithReload(() => import('./pages/marketing/tools/ai-product-description-generator/AIProductDescriptionGenerator'));
const CopyeditorLanding = lazyWithReload(() => import('./pages/marketing/tools/copyeditor/CopyeditorLanding'));
const EssayReviewLanding = lazyWithReload(() => import('./pages/marketing/essay-review/EssayReviewLanding'));
const WorkflowLanding = lazyWithReload(() => import('./components/WorkflowLanding'));
const WorkflowInputPage = lazyWithReload(() => import('./components/WorkflowInputPage/WorkflowInputPage'));
const SkillWizardPage = lazyWithReload(() => import('./pages/tools/skillWizard/SkillWizardPage'));
const StandaloneWizardPage = lazyWithReload(() => import('./apps/SkillWizardApp/components/StandaloneWizardPage'));
const BlogLanding = lazyWithReload(() => import('./components/BlogLanding'));
const SEOBlogsLanding = lazyWithReload(() => import('./pages/marketing/SEOBlogsLanding'));
const GettingStartedPage = lazyWithReload(() => import('./pages/marketing/getting-started/GettingStartedPage'));
const GhostwriterLanding = lazyWithReload(() => import('./pages/marketing/use-cases/ghostwriter/GhostwriterLanding'));
const GrantWriterLanding = lazyWithReload(() => import('./pages/marketing/use-cases/grant-writer/GrantWriterLanding'));
// MarketersLanding deleted — /for/marketers replaced by /for/marketing on content-renderer.
// SPA route redirects via CrossRuntimeRedirect.
// BusinessOSLanding migrated to content-renderer at /business-os
// AgentsLanding moved to content-renderer at /automations. SPA route redirects via CrossRuntimeRedirect.
const StartupAgentsLanding = lazyWithReload(() => import('./pages/marketing/StartupAgentsLanding/StartupAgentsLanding'));
const StudentWorkflowLanding = lazyWithReload(() => import('./pages/marketing/use-cases/students/StudentWorkflowLanding'));

// Other Apps - conditional
const IngestWizardApp = lazyWithReload(() => import('apps/IngestWizard/IngestWizard'));
const NotesMarkdownApp = lazyWithReload(() => import('apps/NotesMarkdownApp/NotesMarkdownApp').then(m => ({ default: m.NotesMarkdownApp })));
const StandaloneEntityPage = lazyWithReload(() => import('./pages/StandaloneEntityPage'));
const StandaloneEntityView = lazyWithReload(() => import('./pages/StandaloneEntityView'));

// Wrapper that reads ?view= from the URL and passes it to ContactsApp
function StandaloneContactsApp() {
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get('view') || undefined;
  return (
    <ContactsApp
      windowId="standalone-contacts"
      spaceId="default"
      initialView={viewParam as any}
      onClose={() => {}}
    />
  );
}

// Wrapper component with key to force remount when space ID changes
function SpacePageWrapperWithKey() {
  const log = createFileLogger('SPACE_PAGE_WRAPPER_WITH_KEY', { enabled: false });
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  log.log('[SpacePageWrapperWithKey] 🔑 Rendering with key:', {
    spaceId: id,
    timestamp: new Date().toISOString()
  });
  
  // ✨ INSTANT NAVIGATION: Add transition class to enable animations
  const isFromHome = (location.state as any)?.isPendingSpace === true;
  const isFromCreationPage = (location.state as any)?.isFromCreationPage === true;
  
  React.useEffect(() => {
    if (isFromHome) {
      // Add class to enable entrance animation
      document.body.classList.add('space-entering-from-home');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-home');
      }, 1500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-home');
      };
    } else if (isFromCreationPage) {
      document.body.classList.add('space-entering-from-creation');
      
      // Remove after animation completes
      const timer = setTimeout(() => {
        document.body.classList.remove('space-entering-from-creation');
      }, 500);
      
      return () => {
        clearTimeout(timer);
        document.body.classList.remove('space-entering-from-creation');
      };
    }
  }, [isFromHome, isFromCreationPage]);
  
  
  // Use space ID as key to force remount only when space actually changes
  return <SpacePageWrapper key={id} />;
}

// Redirect /spaces/:id → /desktops/:id so every space renders in the Desktop
// chrome. Forwards search params and location.state intact so optimistic-create
// flows (isPendingSpace, intake, cachedSpace) keep working.
// Escape hatch: ?adminView=true or ?presentation=standard preserves standard chrome.
const spaceToDesktopLog = createFileLogger('SPACE_DESKTOP_REDIRECT');
function SpaceToDesktopRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const forceStandard = params.get('adminView') === 'true' || params.get('presentation') === 'standard';
  if (forceStandard) {
    return <SpacePageWrapperWithKey />;
  }
  spaceToDesktopLog.info('[SpaceToDesktopRedirect] /spaces → /desktops', { id, search: location.search });
  return (
    <Navigate
      to={{ pathname: `/desktops/${id}`, search: location.search }}
      state={location.state}
      replace
    />
  );
}

// Desktop presentation of a Space at /desktops/:id. Reuses SpacePageWrapper's
// full init pipeline via the presentation flag; keyed remount on space change.
//
// DesktopTransitionLayer is mounted OUTSIDE the key so the new-desktop navigator
// survives the keyed remount it triggers (it would otherwise unmount mid-navigate).
function DesktopPageWrapperWithKey() {
  const { id } = useParams<{ id: string }>();
  return (
    <>
      <DesktopPageWrapper key={id} />
      <DesktopTransitionLayer />
      <DesktopSwipeDragLayer currentId={id} />
    </>
  );
}

function RootHomeApp() {
  const { docId } = useParams<{ docId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Custom navigation handler that doesn't remount the layout
  const handleDocNavigation = useCallback((newDocId: string) => {
    navigate(`/d/${newDocId}`, { replace: false });
  }, [navigate]);
  
  // Handle close - go back to launcher
  const handleClose = useCallback(() => {
    navigate('/', { replace: false });
  }, [navigate]);
  
  // If we're on /d/:docId route, show the document
  if (location.pathname.startsWith('/d/') && docId) {
    return (
      <Suspense fallback={<Loader message="Loading document..." />}>
        <DocumentAccessWrapper 
          windowId="root-docs" 
          spaceId="default" 
          onClose={handleClose} 
          title="Docs"
        />
      </Suspense>
    );
  }
  
  // Otherwise show QuickLauncher
  return (
    <QuickLauncherApp 
      windowId="root-quicklauncher" 
      spaceId="default" 
      onClose={() => {}} 
      title="Quick Launcher"
      onNavigateToDoc={handleDocNavigation}
    />
  );
}

/**
 * /r/:code — Single affiliate referral entry point.
 * Stores the code in localStorage (first-touch-wins) then redirects:
 *   - Authenticated users → / (app loads with AffiliateOfferBanner)
 *   - Unauthenticated users → /signup (ref code survives in localStorage)
 */
function AffiliateRefRoute() {
  const { code } = useParams<{ code: string }>();
  const { isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (code) {
      captureRefCode(code);
    }
  }, [code]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else {
      navigate('/signup', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return null;
}

function RootRoute() {
  const location = useLocation();
  const log = createFileLogger('ROOT_ROUTE', { enabled: false });

  const electronEnv = isElectron();
  log.log('[QL_FLOW] 🏁 RootRoute rendered:', { pathname: location.pathname });

  if (electronEnv) {
    return <Navigate to="/login" replace />;
  }

  // Allow document routes to render even without authentication (for public documents)
  if (location.pathname.startsWith('/d/')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering RootHomeApp (doc route)');
    return <RootHomeApp />;
  }

  // `/` is served by content-renderer SSR (CR `pages/index.tsx`). In production
  // CloudFront's `default_cache_behavior` routes the request to the CR ALB
  // before the SPA ever loads — these branches are only hit when a SPA
  // session navigates client-side to `/` (e.g. clicking the logo). We force
  // a full reload so CloudFront can route to CR.
  //
  // Self-hosted (`riversystem.ai`) is a different deploy that does NOT have
  // CloudFront fronting it; the host check above still returns the
  // self-hosted login page directly.
  if (window.location.hostname.includes('riversystem.ai')) {
    log.log('[QL_FLOW] 🏁 RootRoute: Rendering SelfhostedLoginPage (self-hosted host)');
    return <SelfhostedLoginPage />;
  }
  log.log('[QL_FLOW] 🏁 RootRoute: hard-nav to CR homepage');
  return <CrossRuntimeRedirect to="/" />;
}

interface LoaderProps {
  message?: string;
}

// Cubic-bezier wave: each half-period is a single C command with both control
// points at the peak y. This guarantees C1 continuity at every zero-crossing
// (tangent directions match by symmetry), so there are no kinks.
const LOADER_W = 260;
const LOADER_H = 60;
const LOADER_MID = LOADER_H / 2;
const LOADER_AMP = 16;
const LOADER_HALF_WAVE = 40;
const LOADER_COLOR = "#4a89dc";
const LOADER_KX = 0.37;
const LOADER_SCROLL = LOADER_HALF_WAVE * 2;

const LOADER_PATH = (() => {
  const totalW = LOADER_W + LOADER_SCROLL;
  const halves = Math.ceil(totalW / LOADER_HALF_WAVE) + 1;
  let d = `M 0 ${LOADER_MID}`;
  for (let s = 0; s < halves; s++) {
    const sign = s % 2 === 0 ? 1 : -1;
    const x0 = s * LOADER_HALF_WAVE;
    const x3 = x0 + LOADER_HALF_WAVE;
    const cy = LOADER_MID + sign * LOADER_AMP;
    d += ` C ${x0 + LOADER_HALF_WAVE * LOADER_KX} ${cy},`
       + ` ${x0 + LOADER_HALF_WAVE * (1 - LOADER_KX)} ${cy},`
       + ` ${x3} ${LOADER_MID}`;
  }
  return d;
})();

const LOADER_REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function Loader({ message }: LoaderProps = {}) {
  const displayMessage = message || 'Loading...';
  return (
    <div className="space-loading">
      <svg
        width={LOADER_W}
        height={LOADER_H}
        viewBox={`0 0 ${LOADER_W} ${LOADER_H}`}
        role="img"
        aria-label="Loading"
        style={{ display: 'block', overflow: 'hidden' }}
      >
        <defs>
          <clipPath id="loaderClip">
            <rect x="0" y="0" width={LOADER_W} height={LOADER_H} />
          </clipPath>
        </defs>
        <g clipPath="url(#loaderClip)">
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={16} strokeLinecap="round" opacity={0.06}>
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={8} strokeLinecap="round" opacity={0.18}>
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
          <path d={LOADER_PATH} fill="none" stroke={LOADER_COLOR}
            strokeWidth={4} strokeLinecap="round">
            {!LOADER_REDUCED_MOTION && (
              <animateTransform attributeName="transform" type="translate"
                values={`0,0;${-LOADER_SCROLL},0`} dur="2.4s" repeatCount="indefinite" />
            )}
          </path>
        </g>
      </svg>
      <div style={{
        marginTop: '16px',
        fontSize: '14px',
        color: '#666',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        animation: 'fadeIn 0.3s ease-in',
        minHeight: '21px',
      }}>
        {displayMessage}
      </div>
    </div>
  );
}

// ─── Loader Variant: River ───────────────────────────────────────────────────
// Full-viewport-width SVG that morphs between two organic meander shapes.
// Uses the same cubic-bezier half-period construction as Sinuous, but with
// varying per-bend amplitudes and a slow shape-morph instead of a translate.
const RIVER_VW = 1200;
const RIVER_VH = 90;
const RIVER_MID = RIVER_VH / 2;
const RIVER_COLOR = "#4a89dc";
const RIVER_SEGS = 7;
const RIVER_SEG_W = RIVER_VW / RIVER_SEGS;
const RIVER_KX = 0.37;

function buildRiverPath(amps: number[]): string {
  let d = `M 0 ${RIVER_MID}`;
  for (let s = 0; s < RIVER_SEGS; s++) {
    const sign = s % 2 === 0 ? 1 : -1;
    const x0 = s * RIVER_SEG_W;
    const x3 = x0 + RIVER_SEG_W;
    const cy = RIVER_MID + sign * amps[s];
    d += ` C ${x0 + RIVER_SEG_W * RIVER_KX} ${cy},`
       + ` ${x0 + RIVER_SEG_W * (1 - RIVER_KX)} ${cy},`
       + ` ${x3} ${RIVER_MID}`;
  }
  return d;
}

const RIVER_PATH_A = buildRiverPath([34, 28, 38, 24, 36, 30, 32]);
const RIVER_PATH_B = buildRiverPath([26, 36, 24, 34, 22, 38, 28]);
const RIVER_PATH_C = buildRiverPath([38, 22, 32, 36, 28, 26, 36]);

export function LoaderRiver() {
  const morphValues = `${RIVER_PATH_A};${RIVER_PATH_B};${RIVER_PATH_C};${RIVER_PATH_A}`;
  const morphTimes = '0;0.33;0.66;1';
  const morphSplines = '0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1';
  return (
    <svg
      width="100%"
      viewBox={`0 0 ${RIVER_VW} ${RIVER_VH}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Loading"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* wide glow — river body */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={18} strokeLinecap="round" opacity={0.06}>
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
      {/* mid glow */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={8} strokeLinecap="round" opacity={0.15}>
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
      {/* crisp channel */}
      <path fill="none" stroke={RIVER_COLOR} strokeWidth={2.5} strokeLinecap="round">
        {!LOADER_REDUCED_MOTION
          ? <animate attributeName="d" values={morphValues} keyTimes={morphTimes}
              keySplines={morphSplines} calcMode="spline" dur="7s" repeatCount="indefinite" />
          : <animate attributeName="d" values={RIVER_PATH_A} dur="1s" repeatCount="indefinite" />
        }
      </path>
    </svg>
  );
}

// ─── Dev playground page ─────────────────────────────────────────────────────
function DevLoadingPage() {
  const label: React.CSSProperties = {
    fontSize: '12px', fontWeight: 600, color: '#999',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: '24px',
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
    padding: '32px 48px', display: 'flex', flexDirection: 'column', alignItems: 'center',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f7f8fa',
      fontFamily: 'system-ui, -apple-system, sans-serif', padding: '48px 32px',
    }}>
      <h1 style={{ fontSize: '20px', fontWeight: 600, color: '#111', marginBottom: '8px' }}>
        /dev/loading
      </h1>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '48px' }}>
        Loader animation playground — dev only
      </p>

      {/* Loader */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', marginBottom: '32px' }}>
        <div style={card}>
          <div style={label}>Loader</div>
          <Loader message="Loading your workspace…" />
        </div>
      </div>

      {/* River — full width */}
      <div style={{ ...card, padding: '32px 0 28px', alignItems: 'stretch' }}>
        <div style={{ ...label, paddingLeft: '32px' }}>River</div>
        <LoaderRiver />
      </div>
    </div>
  );
}

// Single-subscription wrapper so OrgPaymentGate only fires one Zustand subscriber.
function OrgPaymentGateWrapper() {
  const paymentGate = useDataspaceContextStore(state => state.paymentGate);
  return (
    <OrgPaymentGate
      isOpen={paymentGate.isOpen}
      orgId={paymentGate.orgId || ''}
      orgName={paymentGate.orgName || ''}
      seatCount={paymentGate.seatCount}
      onPaymentStarted={() => useDataspaceContextStore.getState().hidePaymentGate()}
      onCancel={() => {
        useDataspaceContextStore.getState().hidePaymentGate();
        useDataspaceContextStore.getState().switchToPersonal();
      }}
    />
  );
}

export function App() {
  // Contextual AI modal — opened programmatically by Ambient Intelligence Surface
  const { GlobalCmdKModal, openWithAmbientMessage } = useGlobalCmdKShortcut();

  // Ambient Intelligence Surface — reacts to world events and surfaces suggestions
  const { AmbientBubble } = useAmbientSuggestions({ onAct: openWithAmbientMessage });

  // ✅ Enable Electron tab/navigation shortcuts (Cmd+T, Cmd+W, Cmd+[, Cmd+], Ctrl+Tab)
  useGlobalElectronTabShortcuts();

  // ✅ Enable global shortcuts: Cmd+\ sidebar, Cmd+J chat, Cmd+H home, Cmd+D drive, Cmd+N new thread, Cmd+K search
  useGlobalSpacePanelShortcuts();
  const { GlobalEntitySearchModal } = useGlobalEntitySearchShortcut();
  
  // ✅ Initialize default apps and tools for user
  useDefaultsInitializer();

  // Push current Space context to Electron main process for Quick Entry (Ctrl+Space)
  useQuickEntryContextPush();

  // Listen for navigation commands from the Quick Entry window via main process IPC
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!isElectron()) return;
    const handler = (_event: unknown, { path }: { path: string }) => {
      navigate(path);
    };
    (window as any).electron?.ipcRenderer?.on('app:navigate', handler);
    return () => {
      (window as any).electron?.ipcRenderer?.removeListener('app:navigate', handler);
    };
  }, [navigate]);

  // Reload OTHER tabs when this tab switches accounts, preventing 403 cascades
  // from the wrong token being used mid-session. The initiating tab itself
  // must ignore its own broadcast (BroadcastChannel delivers to every other
  // listener on the channel within the browsing context, including a second
  // listener in the SAME tab) — it navigates itself via
  // multiAccountStore.switchToAccount()'s own resolveLandingDesktopRoute()
  // call, and a blind reload here would race that and strand it on whatever
  // URL was already in the address bar instead of the new account's Desktop.
  React.useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('river_account_switch');
      bc.onmessage = (event) => {
        if (event.data?.tabId === ACCOUNT_SWITCH_TAB_ID) return;
        window.location.reload();
      };
    } catch {
      // BroadcastChannel not available — fall back to storage event detection.
      // Storage events never fire in the tab that made the change, so no
      // self-origin check is needed here.
      const handleStorage = (e: StorageEvent) => {
        if (e.key !== 'authToken' || !e.newValue || !e.oldValue || e.newValue === e.oldValue) return;
        window.location.reload();
      };
      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }
    return () => { bc?.close(); };
  }, []);

  // Global navigation bridge for non-React callers (command handlers, stores).
  // See gui/src/utils/navigation.ts — any module can dispatch `river:navigate`
  // and this listener forwards it to react-router so history stays clean.
  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ path: string; state?: Record<string, unknown>; replace?: boolean }>).detail;
      if (!detail?.path) return;
      navigate(detail.path, { state: detail.state, replace: detail.replace });
    };
    window.addEventListener('river:navigate', handler);
    return () => window.removeEventListener('river:navigate', handler);
  }, [navigate]);

  const location = useLocation(); // still needed for admin-route check on mount
  const log = createFileLogger('APP', { enabled: false });
  const { user } = useAuth();

  // Upgrade the stored landing page from "/" to the first dedicated path the
  // user visits, so signup attribution prefers the most specific landing page
  // they ever touched. No-op once a dedicated path is already stored.
  React.useEffect(() => {
    upgradeLandingPageIfDedicated(location.pathname);
  }, [location.pathname]);

  // Guard: run systemStore init (orgs + context + globalEntityStore bootstrap)
  // at most once per authenticated user. Navigation changes must NOT re-trigger it.
  const systemStoreInitialized = React.useRef(false);
  
  // Settings modal state for URL deep linking (when on pages without SystemMenuButton)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState<string | undefined>();
  const [settingsInitialSection, setSettingsInitialSection] = React.useState<string | undefined>();
  
  // ✨ Initialize promo on first login
  const promoInitialized = React.useRef(false);
  
  React.useEffect(() => {
    if (user && !promoInitialized.current) {
      promoInitialized.current = true;
      import('./stores/promoStore').then(({ default: usePromoStore }) => {
        usePromoStore.getState().markFirstShown();
      }).catch(() => {});
    }
  }, [user]);
  
  // Invite-a-friend modal — triggered from userStore when free user hits 100 credits
  const showInviteFriendModal = useUserStore(state => state.showInviteFriendModal);
  const setShowInviteFriendModal = useUserStore(state => state.setShowInviteFriendModal);
  // Session monitoring for token expiration
  const { isSessionExpiredModalOpen, handleLogin } = useSessionMonitor();

  // "We added the Desktop app" one-time announcement — shown to every user who
  // existed before the Desktop rollout and hasn't dismissed it yet. New
  // signups after the cutoff have only ever known the Desktop, so they never
  // see it.
  const userProfile = useUserStore(state => state.profile);
  const isMobileModeForAnnouncement = useMobileMode();
  const [showDesktopAppAnnouncement, setShowDesktopAppAnnouncement] = React.useState(false);

  React.useEffect(() => {
    if (!userProfile || isMobileModeForAnnouncement) return;
    if (userProfile.skeleton?.onboardingState?.desktopAppAnnouncementSeen) return;
    const createdAtMs = userProfile.created_at ? new Date(userProfile.created_at).getTime() : NaN;
    if (Number.isNaN(createdAtMs) || createdAtMs >= new Date(DESKTOP_ROLLOUT_CUTOFF).getTime()) return;
    setShowDesktopAppAnnouncement(true);
  }, [userProfile, isMobileModeForAnnouncement]);

  // Debug helper: force the announcement open regardless of the seen-flag or
  // account-age gating above, e.g. `window.__showDesktopAppAnnouncement()`.
  React.useEffect(() => {
    (window as any).__showDesktopAppAnnouncement = () => setShowDesktopAppAnnouncement(true);
    return () => { delete (window as any).__showDesktopAppAnnouncement; };
  }, []);

  // Initialize user tracking service on mount. Must run before any analytics calls.
  // Called exactly once; the analytics effect below does NOT repeat this call.
  React.useEffect(() => {
    perf.increment('userTrackingInitCount');
    userTrackingService.init();

    // Skip token-based identification for OAuth callback popups — they share
    // localStorage with the main window and must not modify it.
    const isOAuthCallback = window.location.pathname.includes('/integrations/') && window.location.pathname.includes('/callback');

    log.log('[App] 🔍 CHECKING OAUTH CALLBACK:', {
      pathname: window.location.pathname,
      isOAuthCallback,
    });

    if (isOAuthCallback) {
      log.log('[App] ✅ SKIPPING TOKEN VALIDATION FOR OAUTH CALLBACK PAGE');
      return;
    }

    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('authToken');
    const tokenToUse = idToken || accessToken;

    if (tokenToUse) {
      try {
        const payload = parseJwtPayload(tokenToUse);

        if (!payload) {
          throw new Error('Invalid JWT structure or encoding');
        }

        if (payload.sub) {
          userTrackingService.setUser(payload.sub);

          const hasValidEmail = payload.email && payload.email.trim() !== '';
          const hasValidName = payload.name && payload.name.trim() !== '';

          if (hasValidEmail || hasValidName) {
            userTrackingService.updateUserInfo({
              email: hasValidEmail ? payload.email : undefined,
              name: hasValidName ? payload.name : undefined,
              userId: payload.sub,
            });
          }
        } else {
          throw new Error('Invalid token payload - missing sub field');
        }
      } catch (error) {
        log.error('Error parsing existing auth token for analytics:', error);
        log.warn('[App] Token parsing failed but keeping token - LoginGUI will validate');
      }
    }
  }, []);

  // Global billing polling loop — deferred to idle time since it's not needed before UI is interactive.
  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    let unsubscribe: (() => void) | null = null;
    let lastPlan: any = null;
    let currentInterval: number = 60000;
    let idleHandle: number | ReturnType<typeof setTimeout> | null = null;
    
    const initBillingPolling = async () => {
      const useUserStore = (await import('./stores/userStore')).default;
      const { isFreePlan } = await import('./utils/planUtils');
      
      // Helper to calculate credits remaining
      const getCreditsRemaining = (state: any) => {
        const { currentBill } = state;
        if (!currentBill) return null;
        const usedCents = currentBill.charges?.usedCost || 0;
        const maxCents = currentBill.charges?.monthlyAllowance || currentBill.plan?.monthlyFreeCredits || 50;
        return Math.max(0, maxCents - usedCents);
      };
      
      // Polling interval: free/basic check frequently (overage detection + budget exhaustion).
      // Paid plans (payg/standard/premium) are primarily updated via YJS billing_refresh after each
      // chat stream — this poll is just a background safety net at a lower frequency.
      const getPollingInterval = (plan: string | null, creditsRemaining: number | null) => {
        const isPaidPlan = plan && plan !== 'free' && plan !== 'basic';
        if (isPaidPlan) return 300000; // 5 minutes — safety net only
        if (creditsRemaining !== null && creditsRemaining < 50) {
          log.info('[BILLING_POLLING] Low credits detected, polling every 30s');
          return 30000;
        }
        return 60000;
      };
      
      // Helper to start/restart polling interval
      const startPolling = () => {
        const state = useUserStore.getState();
        const planName = state.plan?.plan ?? null;
        const creditsRemaining = getCreditsRemaining(state);
        const newInterval = getPollingInterval(planName, creditsRemaining);
        
        // Clear existing interval if interval duration changed
        if (intervalId && currentInterval !== newInterval) {
          log.info('[BILLING_POLLING] Interval duration changed, restarting', {
            old: `${currentInterval}ms`,
            new: `${newInterval}ms`
          });
          clearInterval(intervalId);
          intervalId = null;
        }
        
        // Create new interval if needed
        if (!intervalId) {
          currentInterval = newInterval;
          intervalId = setInterval(() => {
            const currentState = useUserStore.getState();
            if (currentState.plan) {
              currentState.fetchCurrentBill();
              
              // After fetch, check if we need to adjust interval (credit level may have dropped)
              const newCreditsRemaining = getCreditsRemaining(currentState);
              const requiredInterval = getPollingInterval(currentState.plan.plan, newCreditsRemaining);
              if (requiredInterval !== currentInterval) {
                startPolling();
              }
            } else {
              if (intervalId) clearInterval(intervalId);
              intervalId = null;
            }
          }, newInterval);
        }
      };
      
      // Subscribe to store changes and check if plan changed
      unsubscribe = useUserStore.subscribe((state) => {
        const { plan, fetchCurrentBill } = state;
        
        // Only react if plan actually changed
        if (plan !== lastPlan) {
          lastPlan = plan;
          
          // Clear existing interval if any
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          if (plan) {
            log.info(`[BILLING_POLLING] ${plan.plan} plan detected - starting billing poll`);
            fetchCurrentBill();
            startPolling();
          }
        }
      });
      
      // Trigger initial check with current plan state
      const { plan, fetchCurrentBill } = useUserStore.getState();
      lastPlan = plan;
      if (plan) {
        log.info(`[BILLING_POLLING] Initial ${plan.plan} plan detected - fetching bill`);
        fetchCurrentBill();
        startPolling();
      }
    };
    
    if ('requestIdleCallback' in window) {
      idleHandle = requestIdleCallback(() => initBillingPolling(), { timeout: 5000 });
    } else {
      idleHandle = setTimeout(() => initBillingPolling(), 3000);
    }
    
    return () => {
      if (idleHandle != null) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(idleHandle as number);
        else clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
      }
      if (intervalId) clearInterval(intervalId);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // When the tab regains focus, refresh HTTP-backed stores that may have drifted
  // while the tab was backgrounded. WebSocket reconnection is handled entirely by
  // wsReconnectCoordinator (registered via wsTokenRefresher) — calling provider.connect()
  // here would bypass its stagger logic and cause a thundering-herd reconnect storm.
  React.useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      // Delay until after wsReconnectCoordinator's settle + first staggered resume
      // so HTTP fetches don't compete with WS handshakes on the same tick.
      setTimeout(() => {
        import('./stores/dataspaceContextStore').then(({ useDataspaceContextStore }) => {
          const store = useDataspaceContextStore.getState();
          const FIVE_MINUTES = 5 * 60 * 1000;
          if (!store.lastOrgLoadAt || Date.now() - store.lastOrgLoadAt > FIVE_MINUTES) {
            store.loadUserOrgs();
          }
          // refreshContext detects cross-device context changes and calls
          // applyRemoteContextChange if the server value differs from local state.
          store.refreshContext();
        }).catch(() => {});
      }, 1000);
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, []);

  // Initialize SystemStore globally (manages spaces and dataspace context).
  // Runs once per authenticated user — route navigation must NOT re-trigger it.
  React.useEffect(() => {
    // Cleanup handles for notification subscriptions created inside this effect.
    let unsubscribeNotifications: (() => void) | null = null;
    let unsubscribeNotificationsReconnect: (() => void) | null = null;

    const initSystemStore = async () => {
      perf.increment('systemStoreInitCount');

      // Skip on admin routes — they don't need entity/context bootstrap.
      if (location.pathname.startsWith('/admin')) {
        log.info('[SYSTEM_STORE_INIT] ⏭️ Skipping init for admin route');
        return;
      }

      const authToken = localStorage.getItem('authToken');
      if (!authToken || !user) return;

      // Once-guard: don't repeat org-load + context-fetch on navigation.
      if (systemStoreInitialized.current) {
        log.info('[SYSTEM_STORE_INIT] Already initialized — skipping repeat run');
        return;
      }
      systemStoreInitialized.current = true;

      // A returning user with a valid/stale token can land on /signup or
      // /login (stale bookmark, back button, marketing link). useAuthForm's
      // own mount-time token check redirects them away almost immediately,
      // but that logic lives in the lazily-loaded signup/login chunk, while
      // `user` here resolves synchronously from a local JWT parse (see
      // useAuth.ts) — so this effect can fire and start opening org/
      // notification websockets before the redirect chunk has even
      // downloaded. Give the redirect a brief head start so it doesn't
      // compete with the signup/login page for bandwidth (and doesn't get
      // attributed to that page's load in monitoring). If no redirect
      // happens in time (e.g. the token turns out to be invalid), proceed
      // anyway — nothing is lost, just delayed.
      if (location.pathname === '/signup' || location.pathname === '/login') {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      perf.increment('systemStoreInitWithUser');
      try {
        const [
          { default: useSystemStore },
          { default: fetchService },
          { API_BASE_URL, getWsIdentitySub },
        ] = await Promise.all([
          import('./stores/systemStore'),
          import('./services/fetchService'),
          import('./api/config'),
        ]);

        // Wire up real-time notifications via the Y.js WebSocket room, in
        // parallel with the org/context network round-trips below. This used
        // to be split into two separate initNotificationsYjsStore() calls (an
        // early fire-and-forget kick-off here, computed from getWsIdentitySub(),
        // plus a second init+subscribe call later in the entity-boot path,
        // recomputed from the same function). initNotificationsYjsStore()'s
        // same-user guard (`_wsHandle && _userId === userId`) silently treats
        // two calls that resolve to different userId values as a user switch
        // and tears down the just-established connection — including
        // `_listeners.clear()` — with no error and no counter incremented.
        // Whenever that raced against the later subscribeNotificationsYjs()
        // call, the App-level toast listener was wiped immediately after being
        // registered, permanently orphaning notifications for the rest of the
        // session with no visible symptom. Doing init+subscribe together, once,
        // from a single resolved userId removes the race entirely. Also kept
        // independent of globalEntityStore's lifecycle (unlike the old second
        // call site) since notification delivery has nothing to do with
        // whether the entity store has been initialized yet.
        // Uses getWsIdentitySub() (parsed from the same authToken sent to the WS
        // server) rather than user.sub — Auth0 account linking can leave user.sub
        // pointing at a different identity than the one the WS server sees,
        // which would make it permanently deny this per-user notification room.
        const notifUserId = getWsIdentitySub() ?? '';
        if (notifUserId) {
          const {
            initNotificationsYjsStore,
            subscribeNotificationsYjs,
            subscribeNotificationsReconnect,
          } = await import('./stores/notificationsYjsStore');
          const { notificationService } = await import('./services/notificationService');

          initNotificationsYjsStore(notifUserId);

          // Resync from MongoDB whenever the notification WS reconnects.
          // The server clears its events Y.Array after each push, so any
          // notification pushed while the client was disconnected is lost
          // from the CRDT — MongoDB has the authoritative copy.
          unsubscribeNotificationsReconnect = subscribeNotificationsReconnect(() => {
            notificationService.loadFromBackend().catch(() => {});
          });

          // Capture the unsubscribe so the cleanup function can tear it down.
          unsubscribeNotifications = subscribeNotificationsYjs((id, action, entry) => {
            if (!entry) return;

            if ((entry as any).type === 'job_status') {
              const ev = entry as any;
              log.info(
                `[App] job_status via notification channel: job=${ev.jobId} ` +
                `status=${ev.status} cancelRequested=${ev.cancelRequested}`
              );
              import('./apps/AutomationsApp/store/automationsStore').then(m => {
                const stores = m.getAllAutomationsStores();
                stores.forEach(store => {
                  store.getState().applyJobStatusEvent({
                    jobId: ev.jobId,
                    status: ev.status,
                    cancelRequested: ev.cancelRequested,
                    progress: ev.progress,
                  });
                });
              });
              return;
            }

            // Agent-run reconciliation signal (no user-facing message). Sync
            // Thread.skeleton.agentRunStatus only — do not toast or tray-upsert.
            if ((entry as any).type === 'thread_status') {
              const ev = entry as any;
              const threadId = ev.threadId;
              const status = ev.status;
              log.info(
                `[App] thread_status via notification channel: thread=${threadId} status=${status}`
              );
              if (threadId && status) {
                window.dispatchEvent(
                  new CustomEvent('thread:agentRunStatus', { detail: { threadId, status } })
                );
              }
              return;
            }

            if ((entry as any).type === 'toast') {
              notificationService.showToastFromEntry(entry as any);
              const threadId = (entry as any).data?.threadId;
              const status = (entry as any).priority === 'high' ? 'failed' : 'succeeded';
              if (threadId) {
                window.dispatchEvent(
                  new CustomEvent('thread:agentRunStatus', { detail: { threadId, status } })
                );
              }
              return;
            }

            if ((entry as any).type === 'automation_message') {
              const ev = entry as any;
              log.info(
                `[App] automation_message notification: msg=${ev.data?.messageId} ` +
                `job=${ev.data?.jobId} space=${ev.data?.spaceId}`
              );
              const msgText = (ev.message || '').trim();
              const previewTitle = ev.title
                || (msgText.length > 50 ? msgText.slice(0, 50).trimEnd() + '…' : msgText || 'Agent message');
              notificationService.upsert({
                id,
                type: 'automation_message',
                title: previewTitle,
                message: ev.message || '',
                status: 'pending',
                read: false,
                timestamp: ev.sentAt ? new Date(ev.sentAt).getTime() : Date.now(),
                jobId: ev.data?.jobId,
                messageId: ev.data?.messageId,
                threadId: ev.data?.threadId,
                automationName: ev.data?.automationName,
                automationId: ev.data?.automationId,
                spaceName: ev.data?.spaceName,
                spaceId: ev.data?.spaceId,
                actionUrl: ev.data?.spaceId
                  ? `/desktops/${ev.data.spaceId}`
                  : undefined,
              });
              return;
            }

            if ((entry as any).type === 'agent_message') {
              const ev = entry as any;
              log.info(`[App] agent_message notification: space=${ev.data?.spaceId}`);
              const msgText = (ev.message || '').trim();
              const previewTitle = ev.title
                || (msgText.length > 50 ? msgText.slice(0, 50).trimEnd() + '…' : msgText || 'Notification');
              notificationService.upsert({
                id,
                type: 'agent_message',
                title: previewTitle,
                message: ev.message || '',
                status: 'succeeded',
                read: false,
                timestamp: ev.sentAt ? new Date(ev.sentAt).getTime() : Date.now(),
                automationName: ev.data?.automationName,
                automationId: ev.data?.automationId,
                spaceName: ev.data?.spaceName,
                spaceId: ev.data?.spaceId,
                actionUrl: ev.data?.spaceId
                  ? `/desktops/${ev.data.spaceId}`
                  : undefined,
              });
              return;
            }

            // Remote notification mutation (from AI tool or another device).
            // Update in-memory state without re-persisting to MongoDB.
            if ((entry as any).type === 'notification_update') {
              const ev = entry as any;
              if (ev.action === 'delete') {
                notificationService.removeLocal(ev.notification_id);
              } else if (ev.action === 'mark_all_read') {
                notificationService.markAllReadLocal();
              } else if (ev.action === 'mark_read') {
                notificationService.markReadLocal(ev.notification_id);
              } else if (ev.action === 'mark_unread') {
                notificationService.markUnreadLocal(ev.notification_id);
              } else if (ev.action === 'status_change') {
                notificationService.updateStatusLocal(ev.notification_id, ev.data?.status, ev.data?.reply);
              }
              return;
            }

            // Another device switched teams — sync all stores immediately so this
            // device's UI and API calls stay consistent with the server-side context.
            if ((entry as any).type === 'context_changed') {
              const ev = entry as any;
              import('./stores/dataspaceContextStore').then(({ useDataspaceContextStore: dsStore }) => {
                dsStore.getState().applyRemoteContextChange(ev.org_id, ev.org_name ?? null);
              }).catch(err => {
                log.warn('[App] context_changed: failed to apply remote context change', err);
              });
              return;
            }

            const e = entry as any;
            const isFailed = e.priority === 'high' || e.priority === 'urgent'
              || e.message?.toLowerCase().includes('fail');

            notificationService.upsert({
              id,
              type: isFailed ? 'automation_error' : 'automation_complete',
              title: isFailed ? 'Automation failed' : 'Automation complete',
              message: e.message || '',
              status: isFailed ? 'failed' : 'succeeded',
              read: e.read === true,
              timestamp: e.sentAt ? new Date(e.sentAt).getTime() : Date.now(),
              jobId: e.data?.jobId,
              spaceId: e.data?.spaceId,
              // Prefer a structured /desktops/:id?thread=:id URL so ToastStack
              // can navigate properly. Fall back to legacy actionUrl if present.
              actionUrl: e.actionUrl ?? (
                e.data?.spaceId && e.data?.threadId
                  ? `/desktops/${e.data.spaceId}?thread=${e.data.threadId}`
                  : e.data?.threadId
                  ? `/agents/${e.data.threadId}`
                  : undefined
              ),
            });

            log.debug('[NOTIFICATIONS] Live notification → popup', id, e.priority);
            notificationService.showToastFromEntry(e);
            const threadId = e.data?.threadId;
            if (threadId) {
              window.dispatchEvent(new CustomEvent('thread:agentRunStatus', {
                detail: { threadId, status: isFailed ? 'failed' : 'succeeded' },
              }));
            }
          });

          // Seed in-memory store with persisted notifications so the bell and
          // NotificationsApp show correct data immediately after a page reload.
          notificationService.loadFromBackend().catch(() => {});

          log.info('[NOTIFICATIONS_INIT] Kicked off notifications Y.js for userId:', notifUserId.slice(-6));
        }

        // Load orgs and fetch authoritative context in parallel — they are independent.
        const [, backendContextResult] = await Promise.allSettled([
          useSystemStore.getState().actions.loadUserOrgs(),
          fetchService(`${API_BASE_URL}/user/context`, { method: 'GET' }),
        ]);

        log.info('[SYSTEM_STORE_INIT] ✅ Organizations loaded');

        // Sync authoritative context into both stores atomically.
        try {
          if (backendContextResult.status === 'rejected') throw backendContextResult.reason;
          const backendContext = backendContextResult.value;

          log.info('[SYSTEM_STORE_INIT] 📥 Fetched context from backend:', {
            type: backendContext.type,
            orgId: backendContext.org_id,
            orgName: backendContext.org_name
          });

          const normalizedContext = {
            type: backendContext.type,
            orgId: backendContext.org_id || null,
            orgName: backendContext.org_name || null
          };

          // Write both stores in the same tick so they are never diverged.
          const { useDataspaceContextStore: dsStore } = await import('./stores/dataspaceContextStore');
          useSystemStore.setState({ currentContext: normalizedContext });
          dsStore.setState({ currentContext: normalizedContext });
          log.info('[SYSTEM_STORE_INIT] ✅ Both context stores synced from backend');
        } catch (contextError) {
          log.warn('[SYSTEM_STORE_INIT] Failed to fetch context from backend, using cached:', contextError);
        }

        log.info('[SYSTEM_STORE_INIT] ✅ System store initialized');

        // Prefetch recent desktops so the system-bar Desktops switcher can paint
        // from cache instead of waiting on GET /api/spaces/list at open time.
        useSystemStore.getState().actions.ensureRecentSpaces().catch(() => {});

        // Align globalEntityStore dataspace with the authoritative context.
        // Org switches are also handled by globalEntityStore's own
        // dataspaceContextStore subscription, so this is a belt-and-braces
        // synchronization for the very first boot.
        const context = useSystemStore.getState().currentContext;
        const expectedDataspaceId = context.orgId ?? null;
        const { useGlobalEntityStore } = await import('./stores/globalEntityStore');
        const currentDataspaceId = useGlobalEntityStore.getState().dataspaceId;

        if (currentDataspaceId !== null && currentDataspaceId !== expectedDataspaceId) {
          log.warn(`[DATASPACE_INIT] Mismatch: context=${expectedDataspaceId ?? 'personal'} store=${currentDataspaceId ?? 'personal'} — resetting`);
          await useGlobalEntityStore.getState().reset();
        }
      } catch (error) {
        log.error('[SYSTEM_STORE_INIT] Failed to initialize system store:', error);
      }

      // Boot path for the new entity system:
      //   1. Read context (orgId or null).
      //   2. Start the org Y.Doc room (live-update bus).
      //   3. Initialize globalEntityStore (Space + Integration* + MCPConnection)
      //      via 3 parallel typed REST calls.
      // (Notifications are wired up earlier, above — independent of this
      // lifecycle guard, since they have nothing to do with entity loading.)
      // Per-space entity loading is owned by SpacePageWrapper; never bulk-loaded.
      const { useGlobalEntityStore } = await import('./stores/globalEntityStore');

      if (useGlobalEntityStore.getState().lifecycle === 'uninitialized') {
        try {
          const useSystemStore = (await import('./stores/systemStore')).default;
          const context = useSystemStore.getState().currentContext;

          // Start entity-system Y.Doc before kicking off the global store init so
          // live updates from the org Y.Doc room are queued by the time the
          // initial REST burst returns.
          //
          // IMPORTANT: Do NOT fall back to user.sub when orgId is null. Connecting
          // the org room as `org:{user.sub}` creates a wrong room that gets
          // destroyed and rebuilt the moment the real orgId arrives from
          // /user/context, stacking 2-8s timeouts mid-load. If orgId is null,
          // wait briefly for the context to be populated (e.g. from the signup
          // response context pre-set in useAuthForm) before connecting.
          let dataspaceId = context.orgId;
          if (!dataspaceId) {
            const { waitForDataspaceContext } = await import('./stores/dataspaceContextStore');
            dataspaceId = await waitForDataspaceContext(3000);
          }
          const { initEntitySystemYjsStore } = await import('./stores/entitySystemYjsStore');
          if (dataspaceId) initEntitySystemYjsStore(dataspaceId);
          log.info('[ENTITY_BOOT] Kicked off entity-system Y.Doc for dataspaceId:', dataspaceId);

          // Boot the global entity slice: Space + Integration* + MCPConnection.
          // ~3 typed REST calls in parallel (~50 entities total). Per-space entity
          // loading is owned by SpacePageWrapper.initializeSpace; never bulk-loaded
          // anywhere. The dataspace key is null for the personal context.
          await useGlobalEntityStore.getState().initialize(context.orgId ?? null);
          log.info('[ENTITY_BOOT] ✅ globalEntityStore ready for dataspaceId:', context.orgId ?? 'personal');
        } catch (error) {
          log.error('[ENTITY_BOOT] Failed:', error);
        }
      }
    };

    initSystemStore();

    return () => {
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
        unsubscribeNotifications = null;
      }
      if (unsubscribeNotificationsReconnect) {
        unsubscribeNotificationsReconnect();
        unsubscribeNotificationsReconnect = null;
      }
    };
  // Depend on user?.sub (a stable primitive), NOT the `user` object itself: Auth0
  // reissues a freshly-decoded `user` object on every silent token refresh even
  // when the identity hasn't changed, which was re-running this effect mid-session.
  // The re-run's cleanup unconditionally tore down the notification subscription
  // (unsubscribeNotifications), but the re-invoked effect body's one-time guard
  // (systemStoreInitialized.current) then skipped re-subscribing — permanently
  // orphaning the notification Y.js connection: the WS stayed connected and kept
  // receiving live events, but nothing was listening, so toasts silently stopped
  // appearing for the rest of the session. location.pathname is also intentionally
  // excluded: this effect must not re-run on navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  // Local registries (entity types, tool manifests) are cheap static-data loads
  // with no dynamic app-bundle imports, so they're safe to fire unconditionally
  // on every route, including public pages like /signup and /login. The command
  // registry (~60 app-module imports) and auth-gated registries (app registry,
  // bundle commands, admin status) are NOT triggered here — they're only ever
  // consumed inside the authenticated app shell, so GUILayout's mount effect
  // triggers them lazily instead. This guarantees zero command/app-registry
  // network activity on any public/marketing/tool-funnel page.
  React.useEffect(() => {
    initializeLocalRegistries().catch(() => {});
  }, []);

  // Register MCP tools for any connected servers once the global entity store
  // is ready. Subscribes to its lifecycle so we don't fire before
  // MCPConnection entities are loaded.
  React.useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const runMCPInit = async () => {
      try {
        const { useGlobalEntityStore } = await import('./stores/globalEntityStore');
        const { mcpAdapter } = await import('./services/mcp/mcpAdapter');
        const { mcpToolRegistry } = await import('./services/mcp/MCPToolRegistry');
        const { registerSelfContainedCommands } = await import('./stores/commandRegistryStore');
        const { entityService } = await import('./services/entityService');

        // MCPConnection is a global type — read from globalEntityStore.
        const mcpConnections = useGlobalEntityStore.getState().getByType('MCPConnection');
        const connected = mcpConnections.filter(
          (e) =>
            (e as any).skeleton?.status === 'connected' &&
            (e as any).skeleton?.manifest
        );

        if (connected.length === 0) {
          // Only count as a bug if entities weren't loaded yet. If lifecycle is
          // 'ready' and connected is empty, the user simply has no connected
          // MCP servers — fine.
          if (useGlobalEntityStore.getState().lifecycle !== 'ready') {
            perf.increment('mcpInitSkippedEmptyEntities');
          }
          return;
        }
        perf.increment('mcpInitRan');
        log.info(`[MCP_INIT] Checking ${connected.length} connected MCP servers`);

        for (const entity of connected) {
          const entityId = entity._id || (entity as any).id;
          try {
            const status = await mcpAdapter.getStatus(entityId);
            if (status.isActive) {
              const commands = mcpToolRegistry.registerConnection(
                entityId,
                entity.name,
                (entity as any).skeleton.manifest,
                (entity as any).skeleton.settings
              );
              registerSelfContainedCommands(commands);
              perf.increment('mcpToolsRegistered', commands.length);
              log.info(`[MCP_INIT] ✅ ${entity.name}: ${commands.length} tools registered`);
            } else {
              log.info(`[MCP_INIT] ${entity.name}: session gone, marking disconnected`);
              entityService.update(entityId, {
                'skeleton.status': 'disconnected',
              } as any).catch(err => log.warn(`[MCP_INIT] mark disconnected failed: ${err}`));
              if ((entity as any).skeleton?.settings?.autoReconnect) {
                log.info(`[MCP_INIT] ${entity.name}: autoReconnect triggered`);
                mcpAdapter.connect(entityId).then(({ manifest }) => {
                  if (manifest) {
                    const commands = mcpToolRegistry.registerConnection(
                      entityId, entity.name, manifest, (entity as any).skeleton.settings
                    );
                    registerSelfContainedCommands(commands);
                  }
                }).catch((err) => {
                  log.warn(`[MCP_INIT] autoReconnect failed for ${entity.name}:`, err);
                });
              }
            }
          } catch (err) {
            log.warn(`[MCP_INIT] failed to check ${entity.name}:`, err);
          }
        }
      } catch (err) {
        log.warn('[MCP_INIT] initialization error (non-fatal):', err);
      }
    };

    // Subscribe to globalEntityStore lifecycle; run MCP init once it's 'ready'
    // (i.e. the initial REST burst including MCPConnection has resolved).
    let ran = false;
    import('./stores/globalEntityStore').then(({ useGlobalEntityStore }) => {
      // Fast path: already ready when this effect runs (e.g. HMR reload).
      if (useGlobalEntityStore.getState().lifecycle === 'ready') {
        ran = true;
        runMCPInit();
        return;
      }
      const unsub = useGlobalEntityStore.subscribe((state) => {
        if (state.lifecycle === 'ready' && !ran) {
          ran = true;
          unsub();
          runMCPInit();
        }
      });
    }).catch(() => {});
  }, []);
  
  // Admin status now initialized in the parallel init effect above.

  // Handle URL-based deep linking to settings (works from ANY page including landing)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const settingsParam = urlParams.get('settings');
    const sectionParam = urlParams.get('section');
    
    if (settingsParam) {
      log.log('[App] 🔗 Detected settings URL deep link:', { tab: settingsParam, section: sectionParam });
      
      // Set state to open modal (works even on landing page without SystemMenuButton)
      setSettingsInitialTab(settingsParam);
      setSettingsInitialSection(sectionParam || undefined);
      setSettingsModalOpen(true);
      
      // Also dispatch event for pages that DO have SystemMenuButton
      window.dispatchEvent(new CustomEvent('openSettings', {
        detail: {
          tab: settingsParam,
          sectionId: sectionParam
        }
      }));
      
      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('settings');
      newUrl.searchParams.delete('section');
      window.history.replaceState({}, '', newUrl);
      
      log.log('[App] ✅ Settings modal opened via URL deep link, URL cleaned');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Initialize navigation prevention to protect against accidental navigation
  React.useEffect(() => {
    enableNavigationPrevention({
      enableGestureBlocking: true,
      enableBeforeUnloadWarning: true,
      enableKeyboardShortcuts: true,
      customWarningMessage: 'You have unsaved changes in your documents. Are you sure you want to leave?'
    });

    log.log('[App] Navigation prevention enabled');
  }, []);

  // Registries now initialized in the parallel init effect above.

  // Analytics init: runs once on mount. Registers all provider adapters behind
  // the analytics facade, then initialises each one.
  useEffect(() => {
    perf.increment('analyticsInitCount');
    const log = createFileLogger('APP_INIT');

    // Register providers once. analytics.init() below will call init() on each.
    analytics.register(new LogProvider());
    analytics.register(new GA4Provider());
    analytics.register(new PostHogProvider());
    analytics.register(new ServerRelayProvider());

    (async () => {
      try {
        await analytics.init({});
        log.debug('Analytics facade initialized');
      } catch (error) {
        log.error('Failed to initialize analytics facade:', error);
      }
    })();

    // PostHog is also initialised directly here (posthog-js must be inited once
    // before the PostHogProvider can use it).
    (async () => {
      try {
        const { configService } = await import('./services/configService');
        const token = await configService.getPostHogProjectToken();
        const host = await configService.getPostHogHost();
        // Must align with analytics facade: posthog-js autocapture runs outside the facade.
        if (token && isTrackingEnabled()) {
          // Dynamic import: posthog-js is excluded from the main bundle and
          // loaded on demand after the init token is fetched.  This avoids
          // adding ~60 KB (gzip) to the render-critical chunk for /signup and
          // other public routes that don't need PostHog at first paint.
          const { default: posthog } = await import('posthog-js');
          posthog.init(token, {
            api_host: host,
            defaults: '2026-01-30',
            person_profiles: 'identified_only',
            // Disable automatic pageview capture — the analytics facade sends
            // $pageview manually on every SPA route change. Leaving this on
            // (PostHog default) would double-count page views.
            capture_pageview: false,
            // Disable autocapture (click/input/form events) — all product events
            // are explicitly declared in the typed EventMap catalog. Autocapture
            // adds off-catalog noise and complicates funnels.
            autocapture: false,
            disable_session_recording: true,
            // Explicit session_recording config prevents rrweb from defaulting to
            // cross-origin iframe access, which throws SecurityError on Stripe Elements
            // and hCaptcha iframes. block_selector prevents rrweb from attempting to
            // snapshot iframe content that is cross-origin.
            session_recording: {
              recordCrossOriginIframes: false,
              maskAllInputs: true,
              maskInputOptions: { password: true },
              blockSelector: 'iframe[src*="js.stripe.com"], iframe[src*="stripe.network"], iframe[src*="hcaptcha.com"]',
            },
          });
          log.debug('PostHog initialized successfully');
        } else if (token) {
          log.debug('PostHog skipped (localhost / private host — use ?debug_analytics=1 to enable)');
        }
      } catch (error) {
        log.error('Failed to initialize PostHog:', error);
      }
    })();

    devOnly(() => {
      import('./utils/consoleDebugCommands').then(({ registerConsoleDebugCommands }) => {
        registerConsoleDebugCommands();
      }).catch(() => {});

      import('./utils/debugDataspace').then(() => {
        console.log('🔍 Dataspace debugging utilities loaded!');
        console.log('   Run: debugDataspace() - Full diagnostic report');
        console.log('   Run: clearEntityCache() - Clear all entity caches');
        console.log('   Run: reloadEntities() - Force reload entities from backend');
        console.log('   Run: switchToOrg("orgId") - Switch to org dataspace');
      }).catch(() => {});
    });

    import('./utils/lazyImports').then(({ preloadCommonModules }) => {
      preloadCommonModules();
    }).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider>
      <SystemContextProvider>
        <SelfHostedLicenseGate>
          <div className="app-container">
            <BootAnimation />
            <Suspense fallback={<Loader message="Loading River..." />}>
            <ScrollToTop />
            <NoIndexByRoute />
            <Routes>
            {/* ==== MAIN APPLICATION ROUTES ==== */}
            <Route
              path="/spaces/:id"
              element={
                <ProtectedRoute>
                  <SpaceToDesktopRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/desktops"
              element={
                <ProtectedRoute>
                  <MissionControl />
                </ProtectedRoute>
              }
            />
            <Route
              path="/desktops/:id"
              element={
                <ProtectedRoute>
                  <DesktopPageWrapperWithKey />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-space"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <SpaceCreationPage
                      onClose={() => {
                        const riverOS = (window as any).riverOS;
                        if (riverOS?.window?.close) {
                          riverOS.window.close();
                        } else {
                          window.history.back();
                        }
                      }}
                    />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/new"
              element={
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <NewSpaceRoute />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* ==== STANDALONE APP ROUTES ==== */}
            <Route
              path="/app/agent-panel"
              element={
                <AgentLayout>
                  <AgentPanelRoot />
                </AgentLayout>
              }
            />
            <Route
              path="/app/vault"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Vault">
                      <PasswordVaultApp windowId="standalone-vault" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/listen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Listen">
                      <ListenApp windowId="standalone-listen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/skill-wizard/:catalogId"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Skill Wizard">
                      <React.Suspense fallback={null}>
                        <StandaloneWizardPage />
                      </React.Suspense>
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/mediagen"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Media Gen">
                      <MediaGenApp windowId="standalone-mediagen" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/promptlibrary"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Prompt Library">
                      <PromptLibraryApp windowId="standalone-promptlibrary" spaceId="default" onClose={() => {}} title="Prompt Library" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/inbox"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Inbox">
                      <InboxApp windowId="standalone-inbox" spaceId="default" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/quicklauncher"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Quick Launcher">
                      <QuickLauncherApp windowId="standalone-quicklauncher" spaceId="default" onClose={() => {}} title="Quick Launcher" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/store"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Store">
                      <StoreApp windowId="standalone-store" spaceId="default" onClose={() => {}} title="Store" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/docs/:docId?"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="River">
                    <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Docs" />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            {/* Canonical shareable entity route — auth optional (public entities work without login) */}
            <Route
              path="/entity/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Public Automations Store catalog — must be before /automations/:entityId */}
            <Route
              path="/automations/store"
              element={
                <Suspense fallback={null}>
                  <AutomationsStorePage />
                </Suspense>
              }
            />

            {/* Type-specific alias: /automations/:id → StandaloneEntityView */}
            <Route
              path="/automations/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Type-specific alias: /sheets/:id → StandaloneEntityView */}
            <Route
              path="/sheets/:entityId"
              element={<StandaloneEntityView />}
            />

            {/* Type-specific aliases generated by entityLinkService capabilities */}
            <Route path="/notes/:entityId" element={<StandaloneEntityView />} />
            <Route path="/pages/:entityId" element={<StandaloneEntityView />} />
            <Route path="/canvas/:entityId" element={<StandaloneEntityView />} />
            <Route path="/slides/:entityId" element={<StandaloneEntityView />} />
            <Route path="/videos/:entityId" element={<StandaloneEntityView />} />
            <Route path="/images/:entityId" element={<StandaloneEntityView />} />
            <Route path="/dashboards/:entityId" element={<StandaloneEntityView />} />
            <Route path="/tasks/:entityId" element={<StandaloneEntityView />} />
            <Route path="/projects/:entityId" element={<StandaloneEntityView />} />
            <Route path="/contacts/:entityId" element={<StandaloneEntityView />} />
            <Route path="/tables/:entityId" element={<StandaloneEntityView />} />
            <Route path="/diagrams/:entityId" element={<StandaloneEntityView />} />
            <Route path="/rules/:entityId" element={<StandaloneEntityView />} />
            <Route path="/skill-wizards/:entityId" element={<StandaloneEntityView />} />
            <Route path="/workflows/:entityId" element={<StandaloneEntityView />} />
            <Route path="/web/:entityId" element={<StandaloneEntityView />} />
            <Route path="/files/:entityId" element={<StandaloneEntityView />} />
            <Route path="/folders/:entityId" element={<StandaloneEntityView />} />

            {/* Legacy /standalone/:id route — kept for backward compat, now points to new page */}
            <Route
              path="/standalone/:entityId"
              element={<StandaloneEntityView />}
            />
            <Route
              path="/app/chat"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Chat">
                      <ChatApp windowId="standalone-chat" spaceId="default" onClose={() => {}} title="Chat" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/entitybrowser2"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Entity Browser Pro">
                      <RiverDriveApp 
                        windowId="standalone-entitybrowser2" 
                        spaceId="default" 
                        onClose={() => {}} 
                        title="Entity Browser Pro" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/banking"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Banking">
                      <BankApp 
                        onClose={() => {}} 
                        title="Banking"
                        windowId="standalone-banking-window" 
                        spaceId="standalone-space" 
                      />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/docs"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Documents">
                      <DocumentAccessWrapper windowId="standalone-docs" spaceId="default" onClose={() => {}} title="Documents" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/tasks"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Tasks" appId="tasks">
                      <TasksApp windowId="standalone-tasks" spaceId="default" onClose={() => {}} title="Tasks" />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/automations"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Automations" appId="automations">
                      <AutomationsApp windowId="standalone-automations" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/contacts"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Contacts" appId="contacts">
                      <StandaloneContactsApp />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/app/mail"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Mail" appId="mail">
                      <StandaloneMailAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/messaging"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Messages" appId="messaging">
                      <StandaloneMessagingAppWrapper />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/bookkeeper"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Bookkeeper">
                      <BookkeeperApp windowId="bookkeeper-standalone" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/ingestwizard"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <StandaloneAppLayout appTitle="Ingest Wizard">
                      <IngestWizardApp windowId="standalone-ingestwizard" spaceId="default" onClose={() => {}} />
                    </StandaloneAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/notes/:noteId?"
              element={
                <ProtectedRoute>
                  <GUILayout pageType="standalone">
                    <ElectronAppLayout>
                      <NotesMarkdownApp windowId="standalone-notes" spaceId="default" onClose={() => {}} />
                    </ElectronAppLayout>
                  </GUILayout>
                </ProtectedRoute>
              }
            />
            <Route path="/app/login" element={<LoginApp />} />
            
            {/* ==== INTEGRATION CALLBACKS (Public - no auth required) ==== */}
            <Route path="/app/settings/integrations/google-drive/callback" element={<GoogleDriveCallbackPage />} />
            <Route path="/oauth/gmail/callback" element={<GmailOAuthCallback />} />
            <Route path="/plaid-oauth" element={<PlaidOAuthCallbackPage />} />
            
            {/* Blog subdomain Auth0 callback - centralized handler for all blog.rivereditor.com subdomains */}
            <Route path="/blog-auth-callback" element={<BlogAuthCallback />} />

            {/* ==== AUTH & LANDING ROUTES ==== */}
            <Route path="/electron-onboarding" element={<ElectronOnboardingPage />} />
            {/* Cinematic first-run onboarding for new Electron installs — Test at: http://localhost:3000/electron-first-run */}
            <Route path="/electron-first-run" element={<ElectronFirstRunPage />} />
            {/* Agents home — list and resume past agent runs. Hidden; kept here so we can re-enable later if needed. */}
            
            <Route
              path="/agents"
              element={
                <ProtectedRoute>
                  <AgentsHome />
                </ProtectedRoute>
              }
            />
           
            {/* Agent conversation page — URL carries threadId so reloads restore exact session */}
            <Route
              path="/agents/:threadId"
              element={
                <ProtectedRoute>
                  <AgentConversationPage />
                </ProtectedRoute>
              }
            />
            {/* Dev-only: onboarding card UI playground — http://localhost:3000/first-run-preview */}
            {IS_DEV && <Route path="/first-run-preview" element={<OnboardingCardPreview />} />}
            {/* Dev-only: tool card state inspector — http://localhost:3000/tool-card-preview */}
            {IS_DEV && <Route path="/tool-card-preview" element={<ToolCardPreview />} />}
            {/* Dev-only: loader animation playground — http://localhost:3000/dev/loading */}
            {IS_DEV && <Route path="/dev/loading" element={<DevLoadingPage />} />}
            {/* Dev-only: procedural background playground — http://localhost:3000/mocks/backgrounds */}
            {IS_DEV && <Route path="/mocks/backgrounds" element={<BackgroundPreview />} />}
            {/* Self-hosted onboarding with welcome animation - Test at: http://localhost:3000/selfhosted-onboarding */}
            <Route path="/selfhosted-onboarding" element={<SelfHostedOnboardingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/selfhosted-login" element={<SelfhostedLoginPage />} />
            {!([
              'bedrock.computer',
              'riversystem.ai',
            ].some(d => window.location.hostname.includes(d))) ? (
              <Route path="/signup" element={<SignupPage />} />
            ) : null}
            
            {/* Affiliate referral entry point — single link for all audiences */}
            <Route path="/r/:code" element={<AffiliateRefRoute />} />
            {/* Admin invite links — parameterizable onboarding for invited users */}
            <Route path="/invite/:token" element={<InvitePage />} />

            {/* Root route: authenticated users see QuickLauncher with chat, others see landing */}
            <Route path="/" element={<RootRoute />} />
            {/* Document route from launcher - shares same layout as root */}
            <Route path="/d/:docId" element={<RootRoute />} />
            
            {/* Public Book Viewer — accessible without an account */}
            <Route path="/book/:entityId" element={<PublicBookViewer />} />

            {/* Showcase Routes - Public */}
            <Route path="/showcase/:userId" element={<ShowcasePage />} />
            <Route path="/showcase/:userId/doc/:docId" element={<ShowcaseDocumentPage />} />
            {/* LEGACY: /onboarding/* paths - keeping for backwards compatibility */}
            <Route path="/onboarding/nsfw" element={<NSFWOnboarding />} />
            <Route path="/onboarding/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-blog-creator" element={<SEOBlogCreatorOnboarding />} />
            <Route path="/onboarding/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/onboarding/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/onboarding/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/onboarding/:persona" element={<TemplateOnboarding />} />
            
            {/* NEW: /tools/* paths - preferred going forward */}
            <Route path="/tools/nsfw" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            <Route path="/tools/blog-creator" element={<BlogCreatorOnboarding />} />
            <Route path="/tools/seo-optimizer" element={<SEOOptimizerOnboarding />} />
            <Route path="/tools/content-polisher" element={<ContentPolisherOnboarding />} />
            <Route path="/tools/image-inserter" element={<ImageInserterOnboarding />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/org-payment-success" element={<OrgPaymentSuccessPage />} />
            <Route path="/invitations/:token" element={<InvitationAcceptPage />} />
            {/* /downloads — ported to content-renderer. CloudFront routes the
                URL to CR; this redirect handles in-SPA <Link> clicks. */}
            <Route path="/downloads" element={<CrossRuntimeRedirect to="/downloads" />} />
            {/* /nsfw → /for/uncensored-ai-writer (canonical). CF handles the 301; this is defense-in-depth. */}
            <Route path="/nsfw" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            {/* /real-estate → /for/real-estate-developers via edge 301 (cloudfront-function-default-viewer-request.js) */}
            {/* /beta-read, /manuscript-feedback, /humanize-ai, /fact-checker — migrated to content-renderer /tools/* */}
            
            {/* Persona Pages - Organized under /personas/ for analytics bucketing */}
            <Route path="/personas/ai-grant-writer" element={<GrantWriters />} />
            {/* /for/uncensored-ai-writer/app is where the interactive workflow
                tool lives now that content-renderer serves /for/uncensored-ai-writer
                as the SSR marketing page. The marketing page CTAs deep-link to
                /app with ?signup=true so anonymous users sign up first.

                /personas/uncensored-ai-writer is kept rendering NSFWAIWriter as a
                safety net for users with the URL bookmarked or in flight, until
                the edge 301 to /for/uncensored-ai-writer takes over in CloudFront.
                Once GSC confirms the equity transfer (~2-6 weeks), this mount can
                go away. */}
            <Route path="/for/uncensored-ai-writer/app" element={<NSFWAIWriter />} />
            <Route path="/personas/uncensored-ai-writer" element={<NSFWAIWriter />} />

            {/* /for/ai-fanfiction-writer/app — interactive workflow for the
                fanfiction-writer audience. Marketing page is content-renderer
                SSR at /for/ai-fanfiction-writer; /personas/ai-fanfiction-writer
                stays mounted until the edge 301 picks up the equity transfer. */}
            <Route path="/for/ai-fanfiction-writer/app" element={<FanfictionWriter />} />
            {/* Phase 1.4 /for/* interactive workflows. Each marketing page is
                content-renderer SSR at /for/{slug}; the /app variant here
                hosts the interactive workflow component for signed-in users. */}
            <Route path="/for/grant-writer/app" element={<GrantWriters />} />
            <Route path="/for/rfp-writer/app" element={<RFPWriters />} />
            <Route path="/for/academic-writer/app" element={<AcademicWriters />} />
            <Route path="/for/family-office-writer/app" element={<HNWWriters />} />
            <Route path="/for/private-workspace/app" element={<PrivateWorkspace />} />
            <Route path="/for/creative-writing/app" element={<CreativeFreedom />} />
            {/* /personas/* SPA fallbacks removed — CF catch-all redirects /personas/* → /for/* */}
            <Route path="/addons/self-published-book" element={<SelfPublishedBookRedirect />} />
            <Route path="/addons/self-published-book/purchase" element={<BookAddonPurchasePage />} />
            {/* /personas/ai-real-estate-developer → /for/real-estate-developers via edge 301 */}

            {/* Dynamic Persona Pages migrated to content-renderer (/personas/[slug].tsx).
                Full-page navigation to /personas/<slug> is now served by CR via CloudFront. */}

            {/* /for/* and flat legacy URLs → canonical /for/<slug> (single-hop, no /personas/ intermediate).
                These use <CrossRuntimeRedirect> rather than <Navigate> because /for/<slug> is served
                by content-renderer, not the SPA — a client-side <Navigate> would fall through to
                the catch-all 404. CloudFront edge 301s catch these before the SPA loads in prod;
                this is the dev/stale-bundle safety net. See gui/src/components/CrossRuntimeRedirect.tsx. */}
            <Route path="/for/startups/fundraising" element={<CrossRuntimeRedirect to="/for/startup-founders" />} />
            <Route path="/for/startups/analytics" element={<CrossRuntimeRedirect to="/for/saas-operators" />} />
            <Route path="/for/startups/marketing" element={<CrossRuntimeRedirect to="/for/marketing-growth-teams" />} />
            <Route path="/for/startups/social-media" element={<CrossRuntimeRedirect to="/for/creators-influencers" />} />
            <Route path="/for/executives/strategy" element={<CrossRuntimeRedirect to="/for/ceos-executives" />} />
            <Route path="/for/executives/chief-of-staff" element={<CrossRuntimeRedirect to="/for/chiefs-of-staff" />} />
            <Route path="/for/executives/board-prep" element={<CrossRuntimeRedirect to="/for/board-investor-relations" />} />
            <Route path="/for/executives/communications" element={<CrossRuntimeRedirect to="/for/ceos-executives" />} />
            <Route path="/for/executives/weekly-briefings" element={<CrossRuntimeRedirect to="/for/chiefs-of-staff" />} />
            <Route path="/for/life/family-office" element={<CrossRuntimeRedirect to="/for/family-office-managers" />} />
            <Route path="/for/life/giving" element={<CrossRuntimeRedirect to="/for/philanthropy-teams" />} />
            <Route path="/for/life/estate-planning" element={<CrossRuntimeRedirect to="/for/estate-planning-teams" />} />
            <Route path="/for/life/concierge" element={<CrossRuntimeRedirect to="/for/busy-professionals" />} />
            <Route path="/for/life/personal-finance" element={<CrossRuntimeRedirect to="/for/busy-professionals" />} />

            {/* Legacy flat URLs → canonical /for/<slug> (cross-runtime — see above). */}
            <Route path="/grant-writing" element={<CrossRuntimeRedirect to="/for/grant-writer" />} />
            <Route path="/ai-grant-writer" element={<CrossRuntimeRedirect to="/for/grant-writer" />} />
            <Route path="/uncensored-ai-writer" element={<CrossRuntimeRedirect to="/for/uncensored-ai-writer" />} />
            <Route path="/private-ai-workspace" element={<CrossRuntimeRedirect to="/for/private-workspace" />} />
            <Route path="/private-workspace" element={<CrossRuntimeRedirect to="/for/private-workspace" />} />
            <Route path="/ai-creative-freedom" element={<CrossRuntimeRedirect to="/for/creative-writing" />} />
            <Route path="/creative-writing-ai" element={<CrossRuntimeRedirect to="/for/creative-writing" />} />
            <Route path="/fanfiction-ai-writer" element={<CrossRuntimeRedirect to="/for/ai-fanfiction-writer" />} />
            <Route path="/ai-fanfiction-writer" element={<CrossRuntimeRedirect to="/for/ai-fanfiction-writer" />} />
            
            {/* Tool Routes */}
            <Route path="/onboarding/humanizer" element={<HumanizerOnboarding />} />
            <Route path="/onboarding/welcome" element={<RiverOnboarding />} />
            
            {/* Space Templates Landing */}
            <Route path="/space-templates" element={<RiverSpacesLanding />} />

            {/* Product Routes */}
            <Route path="/products" element={<ProductsPage />} />
            {/* /river4, /personal-ai — moved to /x/ experimental namespace in content-renderer */}
            <Route path="/human-first" element={<HumanFirst />} />
            <Route path="/ai-personal-computer" element={<AiPersonalComputer />} />
            <Route path="/river-config" element={<RiverConfigFlow />} />
            <Route path="/work" element={<WorkLanding />} />
            <Route path="/chief-of-staff" element={<Navigate to="/work" replace />} />
            <Route path="/concierge" element={<ConciergeRiverLanding />} />
            <Route path="/twitter-agent" element={<TwitterAgentLanding />} />
            <Route path="/linkedin-growth" element={<LinkedInGrowthLanding />} />
            <Route path="/reddit-agent" element={<RedditAgentLanding />} />
            <Route path="/seo-agent" element={<SeoAgentLanding />} />
            <Route path="/customer-success-agent" element={<CustomerSuccessLanding />} />
            <Route path="/auto-responder" element={<AutoResponderLanding />} />
            <Route path="/customer-support-agent" element={<CustomerSupportAgentLanding />} />
            {/* Nested under /claim so it reuses the /claim/* CloudFront behavior,
                robots Disallow, and NoIndexByRoute prefix — no new infra needed.
                The static "toolbox" segment outranks /claim/:packId in React Router. */}
            <Route path="/claim/toolbox/:toolboxId" element={<ClaimToolbox />} />
            <Route path="/claim/:packId" element={<ClaimPack />} />
            <Route path="/try/:packId" element={<GuestStart />} />
            {/* Private, invite-only landing pages prepared for specific prospects. */}
            <Route path="/clients/cm" element={<ChrisLanding />} />
            <Route path="/clients/studio" element={<StudioClient />} />
            <Route path="/clients/davenport-roadhouse" element={<DavenportRoadhouseClient />} />
            <Route path="/clients/practice" element={<PhysicianPracticeClient />} />
            <Route path="/clients/v-dog" element={<VdogClient />} />
            {/* Private, invite-only interactive product demos prepared for specific prospects. */}
            <Route path="/demos/somnoseal" element={<SomnoSealDemo />} />
            <Route path="/demos/somnoseal/chat" element={<SomnoSealChatDemo />} />
            <Route path="/demos/venturecamp" element={<VentureCampDemo />} />
            <Route path="/demos/venturecamp/workspace" element={<VentureCampWorkspaceDemo />} />
            <Route path="/demos/riverlabs" element={<RiverLabsDemo />} />
            <Route path="/demos/riverlabs/workspace" element={<RiverLabsWorkspaceDemo />} />
            <Route path="/demos/santa-barbara-nephrology" element={<SBNephrologyDemo />} />
            <Route path="/demos/santa-barbara-nephrology/workspace" element={<SBNephrologyWorkspaceDemo />} />
            <Route path="/demos/santa-barbara-nephrology/home" element={<SBNHomeDemo />} />
            <Route path="/demos/santa-barbara-nephrology/desktop" element={<SBNDesktopDemo />} />
            <Route path="/demos/flowerama-columbus" element={<FloweramaColumbusDemo />} />
            <Route path="/demos/flowerama-columbus/workspace" element={<FloweramaColumbusWorkspaceDemo />} />
            <Route path="/demos/pickthorn-family-office" element={<PickthornDemo />} />
            <Route path="/demos/pickthorn-family-office/workspace" element={<PickthornWorkspaceDemo />} />

            {/* Static design mock — campaign analytics dashboard card. */}
            <Route path="/mock/campaign" element={<CampaignMock />} />
            {/* Public marketing case studies — indexable, shareable */}
            <Route path="/case-studies/all-vet-care" element={<AllVetCareCaseStudy />} />
            {/* /apps/* — ported to content-renderer. Catch any stale links
                that still load the SPA bundle and bounce to CR. */}
            <Route path="/apps/mail" element={<CrossRuntimeRedirect to="/apps/mail" />} />
            <Route path="/apps/tasks" element={<CrossRuntimeRedirect to="/apps/tasks" />} />
            <Route path="/apps/drive" element={<CrossRuntimeRedirect to="/apps/drive" />} />
            <Route path="/apps/automations" element={<CrossRuntimeRedirect to="/apps/automations" />} />
            <Route path="/apps/image-studio" element={<CrossRuntimeRedirect to="/apps/image-studio" />} />
            <Route path="/apps/video-studio" element={<CrossRuntimeRedirect to="/apps/video-studio" />} />
            <Route path="/apps/transcript" element={<CrossRuntimeRedirect to="/apps/transcript" />} />
            {/* /workspace — ported to content-renderer. */}
            <Route path="/workspace" element={<CrossRuntimeRedirect to="/workspace" />} />
            <Route path="/tasks" element={<TasksAppPage />} />
            <Route path="/os-signup" element={<OSSignupFlow />} />
            <Route path="/configure-river-1" element={<ConfigureRiver1Page />} />
            <Route path="/checkout" element={<RiverConfigFlow />} />
            <Route path="/checkout-old" element={<CheckoutPage />} />
            
            {/* Enterprise Routes — /enterprise/science and /academic-medicine-pi 301 at the edge to /solutions/life-sciences */}
            {/* /enterprise/consulting deleted — no audience, no traffic */}
            <Route path="/enterprise/law" element={<LawyerDocsEditor />} />

            {/* Use Case Routes */}
            <Route path="/use-cases/prelaw-deadline-panic" element={<PreLawDeadlinePanic />} />

            {/* Converter Routes */}
            <Route path="/convert/pdf-to-word" element={<PdfToWordConverter />} />
            <Route path="/manuscript-feedback/discover/:jobId" element={<ManuscriptDiscovery />} />
            <Route path="/manuscript-feedback/analyzing/:jobId" element={<ManuscriptAnalysisProgress />} />
            <Route path="/convert/chatgpt-to-word" element={<ChatGPTToWordConverter />} />
            <Route path="/convert/markdown-to-word" element={<MarkdownToWordConverter />} />

            {/* Tool Routes */}
            <Route path="/tools/youtube-script-creator" element={<YouTubeScriptCreator />} />
            <Route path="/tools/ai-product-description-generator" element={<AIProductDescriptionGenerator />} />
            <Route path="/tools/copyeditor" element={<CopyeditorLanding />} />
            <Route path="/tools/essay" element={<EssayReviewLanding />} />
            
            {/* Workflow Input Page - Must come BEFORE dynamic slug route */}
            <Route path="/tools/:slug/configure" element={<SkillWizardPage />} />
            <Route path="/tools/:slug/app" element={<WorkflowInputPage />} />
            
            {/* NEW: Workflow Landing Pages with dynamic slug */}
            <Route path="/tools/:slug" element={<WorkflowLanding />} />
            
            {/* SEO Blogs Landing Page - For the new blog creation system */}
            <Route path="/seo-blogs" element={<SEOBlogsLanding />} />
            
            {/* Blog Post Pages with dynamic slug */}
            <Route path="/blogs/:slug" element={<BlogLanding />} />
            
            {/* Guide Tool Pages - SPA routes for interactive tools */}
            <Route path="/guides/:slug/app" element={<WorkflowInputPage />} />
            
            {/* Getting Started Guide */}
            <Route path="/getting-started" element={<GettingStartedPage />} />
            
            {/* Grant Workspace Builder — multi-step wizard, accessible signed-out.
                SPA marketing exception: wizard requires SPA bundle (interactive
                step state, draft store, direct /signup navigation). */}
            <Route path="/build/grant-workspace" element={<GrantWorkspaceBuilder />} />

            {/* For [Audience] Pages */}
            <Route path="/for/ghostwriters" element={<GhostwriterLanding />} />
            <Route path="/for/grant-writers" element={<GrantWriterLanding />} />
            {/* /for/marketers retired — replaced by /for/marketing on content-renderer. */}
            <Route path="/for/marketers" element={<CrossRuntimeRedirect to="/for/marketing" />} />
            {/* /for/researchers, /for/startups, /business-os — migrated to content-renderer */}
            {/* /automations — ported to content-renderer (was AgentsLanding). */}
            <Route path="/automations" element={<CrossRuntimeRedirect to="/automations" />} />
            <Route path="/automations/startups" element={<StartupAgentsLanding />} />
            <Route path="/for/students/:workflowSlug" element={<StudentWorkflowLanding />} />
            
            {/* Legacy redirects for backwards compatibility */}
            <Route path="/essay" element={<Navigate to="/tools/essay" replace />} />
            <Route path="/collegeapp" element={<CollegeEssayLanding />} />
            <Route path="/college-essay" element={<Navigate to="/tools/essay" replace />} />

            {/* Labs — internal experiments, noindex, served at /labs/* */}
            <Route path="/labs/avatar"  element={<AvatarPage />} />
            <Route path="/labs/avatar2" element={<AvatarPage2 />} />
            <Route path="/labs/avatar3" element={<AvatarPage3 />} />
            <Route path="/labs/riverbackgrounds" element={<RiverBackgrounds />} />

            {/* ==== ADMIN ROUTES ==== */}
            {/* Setup page — intentionally not behind AdminProtectedRoute so bootstrap works */}
            <Route
              path="/admin/setup"
              element={
                <GUILayout pageType="standalone">
                  <StandaloneAppLayout appTitle="Admin Setup">
                    <AdminSetup />
                  </StandaloneAppLayout>
                </GUILayout>
              }
            />
            {/* Legacy /admin redirects to setup (for bookmarks) */}
            <Route path="/admin" element={<Navigate to="/admin/setup" replace />} />
            {/* Legacy /admin-dashboard redirects to new overview */}
            <Route path="/admin-dashboard" element={<Navigate to="/admin/overview" replace />} />

            {/* Protected nested admin routes — all children share AdminLayout */}
            <Route
              path="/admin/*"
              element={
                <AdminProtectedRoute>
                  <GUILayout pageType="standalone">
                    <AdminLayout />
                  </GUILayout>
                </AdminProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/overview" replace />} />
              <Route path="overview" element={<AdminOverviewRoute />} />
              <Route path="overview/onboarding" element={<AdminOverviewOnboardingRoute />} />
              <Route path="overview/system" element={<AdminOverviewSystemRoute />} />
              <Route path="users" element={<AdminUsersRoute />} />
              <Route path="users/:userId" element={<AdminUserDetailRoute />} />
              <Route path="entities" element={<AdminEntitiesRoute />} />
              <Route path="entities/key-types" element={<AdminEntitiesKeyMetricsRoute />} />
              <Route path="entities/tools" element={<AdminEntitiesToolsRoute />} />
              <Route path="content" element={<AdminContentRoute />} />
              <Route path="growth" element={<AdminGrowthRoute />} />
              <Route path="activity" element={<AdminActivityRoute />} />
              <Route path="conversions" element={<AdminConversionsRoute />} />
              <Route path="signups-by-page" element={<AdminSignupsByPageRoute />} />
              <Route path="broadcast" element={<AdminBroadcastRoute />} />
              <Route path="analytics" element={<AdminAnalyticsOverviewRoute />} />
              <Route path="analytics/qau" element={<AdminAnalyticsQAURoute />} />
              <Route path="analytics/:metric" element={<AdminAnalyticsMetricRoute />} />
              <Route path="activation" element={<AdminActivationRoute />} />
              <Route path="personas" element={<AdminPersonasRoute />} />
              <Route path="llm-usage" element={<AdminLLMUsageRoute />} />
              <Route path="chat-input" element={<AdminChatInputRoute />} />
              <Route path="user-mini-tools" element={<AdminUserMiniToolsRoute />} />
              <Route path="integrations" element={<AdminIntegrationsRoute />} />
            </Route>

            {/* ==== PUBLIC ROUTES (no auth required) ==== */}
            <Route
              path="/broadcast-unsubscribe"
              element={<BroadcastUnsubscribePageLazy />}
            />

            {/* ==== FALLBACK ROUTES ====
                Real 404. Previously this redirected to "/" with HTTP 200, which
                trained Google to treat every unknown URL as a homepage duplicate
                (soft-404). NotFound emits <meta name="robots" content="noindex">
                via Helmet so crawlers drop these URLs from the index. The HTTP
                status is still 200 because CloudFront's custom_error_response
                swaps S3 404s -> 200 + /index.html so SPA routing works; the
                noindex meta is what does the SEO work. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Global Upgrade Modal with Tier Selection — gated on `user` so an
            anonymous /signup visitor never triggers this chunk's static
            confetti/checkout-url imports; a legitimate upgrade always
            requires being signed into an org anyway. */}
        {user && <UpgradeModal />}

        {/* Global Guest Account-Claim Modal (guest preview sessions) — guest
            preview provisions a real backend account + token before this is
            ever relevant, so gating on `user` only excludes truly anonymous
            visitors, not legitimate guest sessions. */}
        {user && <GlobalGuestClaimModal />}

        {/* Invite-a-Friend Modal (free users, 100-credit threshold) — disabled */}
        {/* {showInviteFriendModal && (
          <Suspense fallback={null}>
            <InviteFriendModalLazy onClose={() => setShowInviteFriendModal(false)} subtitle="Get free usage — share River and instantly unlock:" />
          </Suspense>
        )} */}

        {/* "We added the Desktop app" one-time announcement for existing users */}
        {showDesktopAppAnnouncement && (
          <Suspense fallback={null}>
            <DesktopAppAnnouncementModalLazy onClose={() => setShowDesktopAppAnnouncement(false)} />
          </Suspense>
        )}

        {/* Global Tool Consent Modal */}
        <ToolConsentModal />

        {/* Toast containers for all positions actively used in the codebase */}
        <ToastContainer position="top-right" maxToasts={5} />
        <ToastContainer position="top-center" maxToasts={3} />
        <ToastContainer position="bottom-right" maxToasts={3} />
        <ToastContainer position="bottom-left" maxToasts={3} />
        <ToastContainer position="center" maxToasts={3} />

        {/* Entity Picker SDK — single global host for useEntityPicker() / entity_picker_open */}
        <React.Suspense fallback={null}>
          <EntityPickerHost />
        </React.Suspense>

        <ToastStack />

        {/* Broadcast popups — shows the single latest popup once per user */}
        <React.Suspense fallback={null}>
          <PopupHost />
        </React.Suspense>

        {/* Window controls for frameless Electron on Linux/Windows */}
        <WindowControls />

        {/* Auto-update toast notification */}
        <React.Suspense fallback={null}>
          <UpdateToast />
        </React.Suspense>
        {/* Rosetta warning — shown on Apple Silicon Macs running the x64 build */}
        <React.Suspense fallback={null}>
          <RosettaBanner />
        </React.Suspense>
        {/* Screen Recording permission prompt — shown when macOS TCC denies access */}
        <React.Suspense fallback={null}>
          <ScreenRecordingBanner />
        </React.Suspense>
        {/* Session Expired Modal */}
        <SessionExpiredModal
          isOpen={isSessionExpiredModalOpen}
          onLogin={handleLogin}
        />

        {/* RiverOS v1 Purchase Modal */}
        <OSSignupModal />
        
        {/* Org Payment Gate - Blocks unpaid orgs */}
        <OrgPaymentGateWrapper />
        
        {/* Global Settings Modal (for URL deep linking from landing pages) */}
        {settingsModalOpen && (
          <React.Suspense fallback={<div />}>
            <UserSettingsModal
              isOpen={settingsModalOpen}
              onClose={() => {
                setSettingsModalOpen(false);
                setSettingsInitialTab(undefined);
                setSettingsInitialSection(undefined);
              }}
              initialTab={settingsInitialTab as any}
              initialSectionId={settingsInitialSection}
            />
          </React.Suspense>
        )}
        
        {/* Global Contextual AI Modal — opened by Ambient Intelligence Surface */}
        {GlobalCmdKModal}

        {/* Global Entity Search — Cmd+K */}
        {GlobalEntitySearchModal}

        {/* Ambient Intelligence Surface — reactive suggestions triggered by world events */}
        {AmbientBubble}
        
        {/* Y.js Debug Panel — toggle with Ctrl+Shift+Y */}
        <React.Suspense fallback={null}>
          <YjsDebugPanel />
        </React.Suspense>

          </div>
        </SelfHostedLicenseGate>
      </SystemContextProvider>
    </ThemeProvider>
  );
}

export default App;
